import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  AlertTriangle,
  ArrowRightLeft,
  BadgePercent,
  Check,
  ChevronDown,
  ClipboardList,
  Copy,
  Landmark,
  Pencil,
  PiggyBank,
  Plus,
  Trash2,
  TrendingUp,
  UserCheck,
  Wallet,
} from "lucide-react";
import { createPortal } from "react-dom";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useFormContext,
  useWatch,
  type Control,
  type Path,
} from "react-hook-form";

import type { BuyOrderRow } from "../orderForm/components/BuyOrders";
import type { SellOrderRow } from "../orderForm/components/SellOrders";
import { useOrderForm } from "../orderForm/hooks/useOrderForm";
import {
  getInternalTransferTargets,
  type InternalTransferOrder,
  type InternalTransferTarget,
} from "../orderForm/internalTransfer";
import {
  ADVANCED_EKB_OPTIONS,
  CAPGUARD_PORT_TYPES,
  COMPANY_ALLOWED_PORT_TYPES,
  getManagementFeeOptions,
  getExistingAdvancedEKB,
  getModelOptions,
  portfolioTypeOptions,
  SERVICE_TYPE_LABELS,
} from "../orderForm/OrderForm.options";
import { validateAccountNumber } from "../orderForm/OrderForm.rules";
import { OrderFormData } from "../orderForm/OrderForm.schema";
import { isFundClass, SellHoldingsTable } from "./SellHoldingsTable";
import {
  QueryModelPosition,
  QueryProfile,
  QuerySecurity,
} from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { Portfolio, PortfolioReportItem } from "@/api/types";
import { Checkbox } from "@/components/checkbox";
import { PRICELIST_ROWS } from "@/components/Pricelist/Pricelist";
import { Switch } from "@/components/switch";
import {
  FormFieldGroup as Field,
  FormStat as StatChip,
  SegmentedControl,
  ToggleCard,
} from "@/forms/components";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/number";

// Ground-up redesign: only the shared schema/hook/business-rules from
// ../orderForm are reused here — every visual component below is new.
dayjs.extend(customParseFormat);

// ─── Business rules (kept functionally identical to the legacy form) ─────────

const NON_STOCK_CLASS1 = new Set(["L", "ALT", "R", "K", "PF"]);
function isEquityByClass1(code: string): boolean {
  return !!code && !NON_STOCK_CLASS1.has(code);
}

const NON_STOCK_MODEL_CLASS1 = new Set(["1", "12"]);
function isEquityByModelClass1(code: string): boolean {
  return !NON_STOCK_MODEL_CLASS1.has(String(code ?? ""));
}

function getModelEquityFractionFallback(modelType: string): number {
  if (!modelType || modelType === "NOMODEL") return 0;
  if (modelType.includes("RENTER")) return 0;
  if (modelType.includes("TRYGG")) return 0.25;
  if (
    modelType.includes("EIENDOM") ||
    modelType.includes("EKB") ||
    modelType.includes("AKSJER")
  )
    return 1;
  const match = modelType.match(/(\d+)/);
  return match ? Math.min(Number(match[1]), 100) / 100 : 0;
}

function getMinimumMandateAmount(modelType: string | undefined): number {
  if (modelType === "CAPGUARDEKB") return 1_000_000;
  if (modelType === "CAPGUARDEIENDOM") return 750_000;
  return 0;
}

function isFullWithdrawal(withdrawal: number, portfolioValue: number): boolean {
  return (
    Number.isFinite(withdrawal) &&
    Number.isFinite(portfolioValue) &&
    withdrawal > 0 &&
    portfolioValue > 0 &&
    Math.round(withdrawal) === Math.round(portfolioValue)
  );
}

const MODEL_DISPLAY_NAMES: Record<string, string> = {
  CAPGUARDRENTER: "Aktiv Renter",
  CAPGUARDTRYGG: "Aktiv Trygg",
  CAPGUARDEKB: "Aktiv EKB",
  CAPGUARDEIENDOM: "Aktiv Eiendom",
  FONDSOBLIGASJON: "Fondsobligasjoner",
  AKSJER: "Aktiv Aksjer",
  CAPGUARDAKSJER: "Aktiv Aksjer",
  CAPGUARD75: "Aktiv 75/25",
  CAPGUARD60: "Aktiv 60/40",
  CAPGUARD45: "Aktiv 45/55",
  CAPGUARD25: "Aktiv 25/75",
  NORDNET: "Nordnet",
};

const MAX_INSTRUMENT_ROWS = 6;
const FUND_SWAP_TOLERANCE_NOK = 200;
const MAX_SAVINGS_FUNDS = 5;

function fieldPath(name: string) {
  return name as Path<OrderFormData>;
}

type SavingsFundsFieldValues = {
  dpSavingsFunds: { name: string; pct: number }[];
};

const inputCls =
  "w-full tabular-nums rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px] text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/15";
const VALID_ACCOUNT_TOOLTIP = "Kontonummeret har gyldig kontrollsiffer";
const EXISTING_ORDER_MESSAGE =
  "Det finnes allerede en ordre på denne porteføljen. Gå inn og endre den eksisterende ordren.";

type NumericFieldName =
  | "dpAmountToInvest"
  | "dpOnOffAmount"
  | "dpWithdrawal"
  | "dpIpsAnnualSavings"
  | "dpSavingsAmount"
  | "dpWdAmount"
  | "InstrumentAmt1"
  | "InstrumentAmt2"
  | "InstrumentAmt3"
  | "InstrumentAmt4"
  | "InstrumentAmt5"
  | "InstrumentAmt6";

const fmt = (value: number) => formatNumber(value ?? 0);

function errMsg(error?: { message?: unknown }) {
  return typeof error?.message === "string" ? error.message : undefined;
}

function normalizeAccountNumber(value: string) {
  return value.replace(/\D/g, "").slice(0, 11);
}

function formatAccountNumber(value: string) {
  const normalized = normalizeAccountNumber(value);
  return normalized.replace(/^(\d{4})(\d{2})(\d{5})$/, "$1.$2.$3");
}

function isoToNorwegian(value: string) {
  return value ? dayjs(value).format("DD.MM.YYYY") : "";
}

function norwegianToIso(value: string) {
  const parsed = dayjs(value, "DD.MM.YYYY", true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function makeEmptyBuyRow(): BuyOrderRow {
  return {
    id: Math.random().toString(36).slice(2),
    securityId: null,
    instrumentCode: "",
    instrumentName: "",
    class1code: "",
    class2id: "",
    class2code: "",
    isinCode: "",
    type: "",
    limitPrice: "",
    holding: "",
    feePercent: "",
    price: "",
    fx: "1",
    quantity: "",
    amountInput: "",
    duration: "",
  };
}

function makeSellRowFromHolding(item: PortfolioReportItem): SellOrderRow {
  return {
    securityCode: item.security.securityCode,
    securityId: item.security.id,
    name: item.security.name,
    class2code: item.security.classType2?.code ?? "",
    holding: item.amount,
    marketUnitPrice: item.marketUnitPrice,
    marketUnitPriceDate: item.marketUnitPriceDate,
    sellQty: "",
    feePercent: "",
    type: "",
    limitPrice: "",
    duration: "",
  };
}

function mergeSellRows(
  fresh: SellOrderRow[],
  saved: SellOrderRow[]
): SellOrderRow[] {
  if (!saved.length) return fresh;
  const savedMap = new Map(saved.map((row) => [row.securityId, row]));
  return fresh.map((row) => {
    const previous = savedMap.get(row.securityId);
    return previous
      ? {
          ...row,
          sellQty: row.holding > 0 ? previous.sellQty : "",
          feePercent: previous.feePercent,
          type: previous.type,
          limitPrice: previous.limitPrice,
          duration: previous.duration,
        }
      : row;
  });
}

function useFormattedAmount(name: NumericFieldName) {
  const { setValue } = useFormContext<OrderFormData>();
  const watched = useWatch({ name }) as number | undefined;
  const raw = watched ?? 0;
  const [focused, setFocused] = useState(false);
  const [display, setDisplay] = useState("");

  useEffect(() => {
    if (!focused) setDisplay(raw ? fmt(raw) : "");
  }, [raw, focused]);

  const apply = (value: number) => {
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
    setFocused(false);
    setDisplay(value ? fmt(value) : "");
  };

  const fieldProps = {
    value: display,
    inputMode: "numeric" as const,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      setDisplay(raw ? String(raw) : "");
      const el = e.target;
      requestAnimationFrame(() => el.select());
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = e.target.value
        .replace(/[\s\u00a0]/g, "")
        .replace(/^0+(?=\d)/, "");
      setDisplay(cleaned);
      const num = cleaned === "" ? 0 : Number(cleaned);
      if (!isNaN(num)) setValue(name, num, { shouldValidate: true });
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      const cleaned = e.target.value.replace(/[\s\u00a0]/g, "");
      const num = cleaned === "" ? 0 : Number(cleaned);
      if (!isNaN(num)) setValue(name, num, { shouldValidate: true });
      setFocused(false);
      setDisplay(num ? fmt(num) : "");
    },
  };

  return { raw, apply, fieldProps };
}

function useFormattedAccountNumber() {
  const { setValue } = useFormContext<OrderFormData>();
  const watched = useWatch({ name: "dpNewAccount" }) as string | undefined;
  const raw = watched ?? "";
  const [focused, setFocused] = useState(false);

  const fieldProps = {
    value: focused ? raw : formatAccountNumber(raw),
    inputMode: "numeric" as const,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      const el = e.target;
      requestAnimationFrame(() => el.select());
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue("dpNewAccount", normalizeAccountNumber(e.target.value), {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    onBlur: () => setFocused(false),
  };

  return { fieldProps };
}

function useBuyOrders() {
  const { setValue } = useFormContext<OrderFormData>();
  const rows = (useWatch({ name: "buyOrders" }) as BuyOrderRow[]) ?? [];
  const update = (next: BuyOrderRow[]) =>
    setValue("buyOrders", next, { shouldValidate: true, shouldDirty: true });
  return { rows, update };
}

function useSellOrders() {
  const { setValue } = useFormContext<OrderFormData>();
  const rows = (useWatch({ name: "sellOrders" }) as SellOrderRow[]) ?? [];
  const update = (next: SellOrderRow[]) =>
    setValue("sellOrders", next, { shouldValidate: true, shouldDirty: true });
  return { rows, update };
}

function AmountField({
  name,
  label,
  error,
  disabled,
  suffix = "NOK",
}: {
  name: NumericFieldName;
  label: string;
  error?: string;
  disabled?: boolean;
  suffix?: string;
}) {
  const amount = useFormattedAmount(name);
  return (
    <Field label={label} error={error}>
      <div className="relative">
        <input
          {...amount.fieldProps}
          disabled={disabled}
          placeholder="0"
          className={cn(inputCls, "pl-10", disabled && "opacity-60")}
        />
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium leading-none text-muted-foreground">
          {suffix}
        </span>
      </div>
    </Field>
  );
}

// ─── Portfolio rail (left column) ──────────────────────────────────────────────

function SubHeading({ title }: { title: string }) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {title}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );
}

function PortfolioRail({
  portfolios,
  blockedPortfolioIds,
  editingPortfolioId,
  isCompany,
  selectedId,
  onSelect,
  error,
}: {
  portfolios: Portfolio[];
  blockedPortfolioIds: ReadonlySet<string>;
  editingPortfolioId?: string;
  isCompany: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState("");
  const available = useMemo(
    () =>
      portfolios.filter(
        (p) => !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(p.typeCode)
      ),
    [portfolios, isCompany]
  );
  const normalized = query.trim().toLowerCase();
  const filtered = normalized
    ? available.filter((p) =>
        `${p.shortName ?? ""} ${p.name}`.toLowerCase().includes(normalized)
      )
    : available;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-3 pb-2 pt-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Porteføljer
        </span>
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
          {available.length}
        </span>
      </div>

      <div className="space-y-2 border-b border-border/60 px-3 pb-3">
        <button
          type="button"
          aria-pressed={selectedId === "NEW"}
          onClick={() => onSelect("NEW")}
          className={cn(
            "flex w-full items-center gap-2 rounded-lg border border-dashed px-2.5 py-2 text-left transition-colors",
            selectedId === "NEW"
              ? "border-primary bg-primary/5 text-primary"
              : "border-border text-muted-foreground hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
          )}
        >
          <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Plus className="size-3.5" />
          </span>
          <span className="text-[12px] font-semibold">Ny portefølje</span>
        </button>
        {error && (
          <p className="text-[11px] font-medium text-destructive">{error}</p>
        )}
      </div>

      {available.length > 6 && (
        <div className="px-3 pb-2 pt-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk portefølje"
            className={cn(inputCls, "py-1")}
          />
        </div>
      )}

      <div
        className={cn(
          "min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 pb-2",
          available.length <= 6 && "pt-3"
        )}
      >
        {filtered.map((p) => {
          const id = String(p.id);
          const isActive = selectedId === id;
          const isBlocked = blockedPortfolioIds.has(id);
          const isEditing = editingPortfolioId === id && !isBlocked;
          const total =
            (p.portfolioReport?.positionMarketValue ?? 0) +
            (p.portfolioReport?.accountBalance ?? 0);
          const typeLabel =
            portfolioTypeOptions.find((o) => o.value === p.typeCode)?.label ??
            p.typeCode;
          const modelCode = p.modelPortfolio?.linkedPortfolio?.shortName;
          const modelName = modelCode
            ? MODEL_DISPLAY_NAMES[modelCode] ??
              p.modelPortfolio?.linkedPortfolio?.name ??
              p.modelPortfolio?.name ??
              modelCode
            : undefined;
          return (
            <button
              key={p.id}
              type="button"
              disabled={isBlocked}
              aria-pressed={isActive}
              onClick={() => onSelect(id)}
              className={cn(
                "w-full rounded-lg border px-2.5 py-1.5 text-left transition-colors disabled:cursor-not-allowed",
                isBlocked
                  ? "border-border/60 bg-muted/30"
                  : isActive
                  ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20"
                  : "border-border/60 bg-background hover:border-primary/40 hover:bg-muted/40"
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-foreground">
                  {p.name}
                </span>
                <span
                  className={cn(
                    "max-w-[50%] shrink-0 truncate rounded px-1.5 py-px text-[10px] font-medium",
                    modelName
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {modelName ?? "Ingen modell"}
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                <span className="min-w-0 truncate">
                  {[p.shortName, typeLabel].filter(Boolean).join(" · ")}
                </span>
                <span className="shrink-0 font-medium tabular-nums text-foreground/70">
                  {fmt(total)}
                </span>
              </div>
              {(isBlocked || isEditing) && (
                <div className="mt-1.5">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-[10px] font-medium",
                      isEditing ? "text-primary" : "text-muted-foreground"
                    )}
                  >
                    {isEditing ? (
                      <Pencil aria-hidden="true" className="size-3" />
                    ) : (
                      <ClipboardList aria-hidden="true" className="size-3" />
                    )}
                    {isEditing ? "Redigerer ordre" : "Ordre finnes"}
                  </span>
                </div>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="rounded-lg border border-dashed border-border/70 p-2.5 text-[11px] text-muted-foreground">
            Ingen porteføljer funnet
          </p>
        )}
      </div>
      {blockedPortfolioIds.size > 0 && (
        <p className="border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
          Porteføljer med ordre endres fra dokumentlisten.
        </p>
      )}
    </div>
  );
}

// ─── Portfolio header (top of the form) ────────────────────────────────────────

function PortfolioHeader({
  isCompany,
  selected,
  isNew,
  isTransferAccount,
  onJumpToTransfer,
  onChangeServiceType,
}: {
  isCompany: boolean;
  selected?: Portfolio;
  isNew: boolean;
  isTransferAccount: boolean;
  onJumpToTransfer: () => void;
  onChangeServiceType: () => void;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const portType = useWatch({ name: "dpPortType" }) as string;
  const serviceType = useWatch({ name: "dpServiceType" }) as string | undefined;
  const ServiceIcon = SERVICE_TYPE_OPTIONS.find(
    (option) => option.value === serviceType
  )?.icon;
  const [shortNameCopied, setShortNameCopied] = useState(false);

  const copyShortName = async () => {
    if (!selected?.shortName) return;
    await navigator.clipboard.writeText(selected.shortName);
    setShortNameCopied(true);
    window.setTimeout(() => setShortNameCopied(false), 1_500);
  };

  const typeLabel =
    portfolioTypeOptions.find((o) => o.value === portType)?.label ?? portType;
  const positions = selected?.portfolioReport?.positionMarketValue ?? 0;
  const cash = selected?.portfolioReport?.accountBalance ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <h2 className="min-w-0 truncate text-[15px] font-semibold leading-tight text-foreground">
          {selected?.name ?? "Ny portefølje"}
        </h2>
        {!isNew && typeLabel && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
            {typeLabel}
          </span>
        )}
        {selected?.shortName && (
          <MuiTooltip
            arrow
            placement="top"
            title={
              shortNameCopied
                ? "Porteføljenummer kopiert"
                : "Kopier porteføljenummer"
            }
          >
            <button
              type="button"
              onClick={copyShortName}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-primary"
            >
              {shortNameCopied ? (
                <Check className="size-3 text-primary" />
              ) : (
                <Copy className="size-3" />
              )}
              {selected.shortName}
            </button>
          </MuiTooltip>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isTransferAccount && (
            <button
              type="button"
              onClick={onJumpToTransfer}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/10"
            >
              Flyttefullmakt
            </button>
          )}
          {serviceType && (
            <MuiTooltip arrow placement="top" title="Endre tjeneste">
              <button
                type="button"
                aria-label={`Tjeneste: ${SERVICE_TYPE_LABELS[serviceType]}. Endre`}
                onClick={onChangeServiceType}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 py-1 pl-2.5 pr-2 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/15"
              >
                {ServiceIcon && <ServiceIcon className="size-3.5" />}
                <span className="font-normal text-primary/70">Tjeneste:</span>
                <span>{SERVICE_TYPE_LABELS[serviceType]}</span>
                <Pencil className="size-3 opacity-60" />
              </button>
            </MuiTooltip>
          )}
        </div>
      </div>

      {isNew && (
        <div className="max-w-[260px]">
          <Field label="Type" error={errMsg(errors.dpPortType)}>
            <select {...register("dpPortType")} className={inputCls}>
              <option value="">— velg type —</option>
              {portfolioTypeOptions
                .filter(
                  (o) => !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(o.value)
                )
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
          </Field>
        </div>
      )}

      {selected && (
        <div className="grid grid-cols-3 divide-x divide-border/60 rounded-lg border border-border/60 bg-muted/30">
          {[
            { label: "Verdipapirer", value: positions, highlight: false },
            { label: "Cash", value: cash, highlight: false },
            { label: "Total", value: positions + cash, highlight: true },
          ].map((stat) => (
            <div key={stat.label} className="px-3 py-1.5">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                {stat.label}
              </p>
              <p
                className={cn(
                  "text-[13px] font-bold tabular-nums tracking-tight",
                  stat.highlight ? "text-primary" : "text-foreground"
                )}
              >
                NOK {fmt(stat.value)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Strategy & investment section ─────────────────────────────────────────────

const AMOUNT_CHIPS = [
  { label: "10K", value: 10_000 },
  { label: "100K", value: 100_000 },
  { label: "500K", value: 500_000 },
  { label: "1M", value: 1_000_000 },
  { label: "5M", value: 5_000_000 },
];

function CoreFields({
  targetPortfolios,
  portType,
  modelType,
  modelOptions,
  showMinWarn,
  minThreshold,
  totalPortValue,
  portfolioMarketValue,
  feeOptions,
  modelIsSet,
  priceListOpen,
  onTogglePriceList,
  accountNumbers,
  bankAccount,
  isNew,
  isCapguardPort,
  onCopyAmount,
}: {
  targetPortfolios: InternalTransferTarget[];
  portType: string;
  modelType: string;
  modelOptions: { value: string; label: string }[];
  showMinWarn: boolean;
  minThreshold: number;
  totalPortValue: number;
  portfolioMarketValue: number;
  feeOptions: { value: string; label: string }[];
  modelIsSet: boolean;
  priceListOpen: boolean;
  onTogglePriceList: () => void;
  accountNumbers: string[];
  bankAccount: string;
  isNew: boolean;
  isCapguardPort: boolean;
  onCopyAmount: () => void;
}) {
  const {
    register,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const feeType = useWatch({ name: "dpFeeType" }) as "onlyFund" | "all";
  const amount = useFormattedAmount("dpAmountToInvest");
  const withdrawal = useFormattedAmount("dpWithdrawal");
  const oneOff = useFormattedAmount("dpOnOffAmount");
  const portfolioId = useWatch({ control, name: "dpPortfolioId" });
  const closeAccount = useWatch({ control, name: "dpClosePortfolio" }) ?? false;
  const canCloseAccount =
    !!portfolioId &&
    !isNew &&
    isFullWithdrawal(withdrawal.raw, portfolioMarketValue);
  const closeAccountCheckboxId = `order-close-account-${portfolioId}`;
  useEffect(() => {
    if (!canCloseAccount && closeAccount) {
      setValue("dpClosePortfolio", false, { shouldDirty: true });
    }
  }, [canCloseAccount, closeAccount, setValue]);
  const internalTransfer =
    useWatch({ control, name: "dpWithdrawalInternalTransfer" }) ?? false;
  const targetPortfolioId =
    useWatch({ control, name: "dpWithdrawalTargetPortfolioId" }) ?? "";
  const canTransferInternally = targetPortfolios.length > 0;
  const internalTransferCheckboxId = `order-internal-transfer-${portfolioId}`;
  useEffect(() => {
    if (
      (isNew || withdrawal.raw <= 0 || !canTransferInternally) &&
      internalTransfer
    ) {
      setValue("dpWithdrawalInternalTransfer", false, { shouldDirty: true });
    }
    if (
      targetPortfolioId &&
      (isNew ||
        withdrawal.raw <= 0 ||
        !internalTransfer ||
        !targetPortfolios.some(
          (portfolio) => String(portfolio.id) === targetPortfolioId
        ))
    ) {
      setValue("dpWithdrawalTargetPortfolioId", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    isNew,
    withdrawal.raw,
    internalTransfer,
    targetPortfolioId,
    targetPortfolios,
    canTransferInternally,
    setValue,
  ]);
  const newAccount = useFormattedAccountNumber();
  const newAccountValue = (useWatch({ name: "dpNewAccount" }) ?? "") as string;
  const newAccountCleaned = normalizeAccountNumber(newAccountValue);
  const newAccountHint =
    newAccountCleaned.length === 11 && !validateAccountNumber(newAccountCleaned)
      ? "Kontrollsiffer stemmer ikke – sjekk kontonummeret"
      : undefined;

  return (
    <div className="grid grid-cols-12 gap-x-3 gap-y-3.5">
      <div className="col-span-12 sm:col-span-6">
        <Field label="Modellportefølje" error={errMsg(errors.dpModelType)}>
          <select
            {...register("dpModelType", {
              onChange: (event) => {
                const model = modelOptions.find(
                  (option) => option.value === event.target.value
                );
                setValue(
                  "dpModelTypeName",
                  model?.value === "NOMODEL" ? "" : model?.label ?? "",
                  {
                    shouldDirty: true,
                  }
                );
              },
            })}
            disabled={!portType}
            className={inputCls}
          >
            <option value="">— velg modell —</option>
            {modelOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="col-span-12 sm:col-span-6">
        <Field
          label="Beløp å investere"
          error={errMsg(errors.dpAmountToInvest)}
        >
          <div className="relative">
            <input
              {...amount.fieldProps}
              placeholder="0"
              className={cn(inputCls, "pl-10")}
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium leading-none text-muted-foreground">
              NOK
            </span>
          </div>
        </Field>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {AMOUNT_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => amount.apply(amount.raw + chip.value)}
              className="rounded-md border border-border px-2 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              +{chip.label}
            </button>
          ))}
          {amount.raw > 0 && (
            <button
              type="button"
              onClick={() => amount.apply(0)}
              className="rounded-md border border-border px-2 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
            >
              Nullstill
            </button>
          )}
        </div>
      </div>

      {showMinWarn && (
        <div className="col-span-12 flex gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            {modelType === "CAPGUARDEKB" ? "Aktiv EKB" : "Aktiv Eiendom"} krever
            min. <span className="font-semibold">NOK {fmt(minThreshold)}</span>{" "}
            — mangler{" "}
            <span className="font-semibold">
              NOK {fmt(minThreshold - totalPortValue)}
            </span>
            .
          </p>
        </div>
      )}

      <div className="col-span-12 sm:col-span-4">
        <Field
          label="Tegningshonorar %"
          error={errMsg(errors.dpOnOffPercentage)}
        >
          <input
            type="text"
            {...register("dpOnOffPercentage")}
            placeholder="0"
            className={inputCls}
            onBlur={(e) => {
              const cleaned = e.target.value.replace(/^0+(?=\d)/, "");
              if (cleaned !== e.target.value)
                setValue("dpOnOffPercentage", cleaned);
            }}
          />
        </Field>
      </div>
      <div className="col-span-12 sm:col-span-4">
        <Field
          label="Forvaltningshonorar"
          hint={
            <button
              type="button"
              onClick={onTogglePriceList}
              className={cn(
                "text-[10px] font-semibold uppercase tracking-wide transition-colors",
                priceListOpen
                  ? "text-primary"
                  : "text-muted-foreground hover:text-primary"
              )}
            >
              Prisliste
            </button>
          }
        >
          <select {...register("dpManagementFee")} className={inputCls}>
            {feeOptions.map((o) => (
              <option
                key={o.value}
                value={o.value}
                disabled={o.value === "1.5" && !modelIsSet}
              >
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="col-span-12 sm:col-span-4">
        <Field label="Fee type">
          <SegmentedControl
            value={feeType}
            disabled={modelIsSet}
            onChange={(value) =>
              setValue("dpFeeType", value, { shouldDirty: true })
            }
            options={[
              { value: "onlyFund", label: "Only fund" },
              { value: "all", label: "All" },
            ]}
          />
        </Field>
      </div>

      <div className="col-span-12 sm:col-span-4">
        <Field label="Bankkonto" error={errMsg(errors.dpAccount)}>
          <select {...register("dpAccount")} className={inputCls}>
            <option value="">— velg konto —</option>
            <option value="newAccount">Ny konto</option>
            {accountNumbers.length > 0 && <option disabled>──────────</option>}
            {accountNumbers.map((n) => (
              <option key={n} value={n}>
                {formatAccountNumber(n)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {bankAccount === "newAccount" && (
        <div className="col-span-12 sm:col-span-4">
          <Field label="Nytt kontonummer (11 siffer)">
            <div className="relative">
              <input
                type="text"
                {...newAccount.fieldProps}
                placeholder="XXXX XX XXXXX"
                className={cn(inputCls, "pr-8")}
              />
              {newAccountCleaned.length === 11 && (
                <MuiTooltip
                  arrow
                  placement="top"
                  title={newAccountHint ?? VALID_ACCOUNT_TOOLTIP}
                >
                  <span className="absolute right-2 top-1/2 -translate-y-1/2">
                    {newAccountHint ? (
                      <AlertTriangle className="size-4 text-amber-600" />
                    ) : (
                      <Check className="size-4 text-emerald-600" />
                    )}
                  </span>
                </MuiTooltip>
              )}
            </div>
          </Field>
        </div>
      )}
      <MuiTooltip
        arrow
        placement="top"
        title={
          !isCapguardPort
            ? "Engangstrekk er kun tilgjengelig for Capguard-porteføljer."
            : ""
        }
      >
        <div
          className={cn(
            "col-span-12 sm:col-span-4",
            !isCapguardPort && "cursor-not-allowed"
          )}
        >
          <Field label="Engangstrekk" error={errMsg(errors.dpOnOffAmount)}>
            <div className="relative">
              <input
                {...oneOff.fieldProps}
                disabled={!isCapguardPort}
                placeholder="0"
                className={cn(
                  inputCls,
                  "pl-10",
                  !isCapguardPort &&
                    "pointer-events-none border-dashed bg-muted text-muted-foreground shadow-none"
                )}
              />
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium leading-none text-muted-foreground">
                NOK
              </span>
              <MuiTooltip
                arrow
                placement="top"
                title={
                  isCapguardPort ? "Hent beløpet fra «Beløp å investere»" : ""
                }
              >
                <span className="absolute right-1 top-1/2 flex size-6 -translate-y-1/2">
                  <button
                    type="button"
                    aria-label="Hent beløp fra 'Beløp å investere'"
                    disabled={!isCapguardPort}
                    onClick={onCopyAmount}
                    className="flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary disabled:pointer-events-none disabled:opacity-40"
                  >
                    <Copy className="size-3.5" />
                  </button>
                </span>
              </MuiTooltip>
            </div>
          </Field>
        </div>
      </MuiTooltip>
      <MuiTooltip
        arrow
        placement="top"
        title={
          !isCapguardPort
            ? "Dato for engangstrekk er kun tilgjengelig for Capguard-porteføljer."
            : ""
        }
      >
        <div
          className={cn(
            "col-span-12 sm:col-span-4",
            !isCapguardPort && "cursor-not-allowed"
          )}
        >
          <Field
            label="Dato for engangstrekk"
            error={errMsg(errors.dpOnOffDate)}
          >
            <Controller
              control={control}
              name="dpOnOffDate"
              render={({ field }) => (
                <input
                  type="date"
                  disabled={!isCapguardPort}
                  value={field.value ? norwegianToIso(field.value) : ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? isoToNorwegian(e.target.value) : ""
                    )
                  }
                  className={cn(
                    inputCls,
                    !isCapguardPort &&
                      "pointer-events-none border-dashed bg-muted text-muted-foreground shadow-none"
                  )}
                />
              )}
            />
          </Field>
        </div>
      </MuiTooltip>
      {!isNew && (
        <>
          <div className="col-span-12 sm:col-span-4">
            <Field label="Uttak" error={errMsg(errors.dpWithdrawal)}>
              <div className="relative">
                <input
                  {...withdrawal.fieldProps}
                  min={0}
                  step={1000}
                  placeholder="0"
                  className={cn(inputCls, "pl-10")}
                />
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium leading-none text-muted-foreground">
                  NOK
                </span>
              </div>
            </Field>
            {canCloseAccount && (
              <label
                htmlFor={closeAccountCheckboxId}
                className="mt-2 flex min-h-[34px] cursor-pointer items-center gap-2 rounded-lg border border-border bg-muted/30 px-2.5 py-1.5 text-[11px] font-medium"
              >
                <Checkbox
                  id={closeAccountCheckboxId}
                  checked={closeAccount}
                  onCheckedChange={(checked) =>
                    setValue("dpClosePortfolio", checked === true, {
                      shouldDirty: true,
                    })
                  }
                />
                Porteføljen ønskes avsluttet
              </label>
            )}
          </div>
          {withdrawal.raw > 0 && (
            <div className="col-span-12 grid grid-cols-1 items-start gap-3 sm:col-span-8 sm:grid-cols-2">
              <div className="sm:pt-5">
                <label
                  htmlFor={internalTransferCheckboxId}
                  title={
                    !canTransferInternally
                      ? "Intern overføring er kun mulig mellom to Capguard-porteføljer hos samme kunde."
                      : undefined
                  }
                  className={cn(
                    "flex min-h-[34px] items-center gap-2 rounded-lg border border-border bg-muted/30 px-2.5 py-1.5 text-[11px] font-medium",
                    canTransferInternally
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-50"
                  )}
                >
                  <Checkbox
                    id={internalTransferCheckboxId}
                    disabled={!canTransferInternally}
                    checked={internalTransfer}
                    onCheckedChange={(checked) =>
                      setValue(
                        "dpWithdrawalInternalTransfer",
                        checked === true,
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        }
                      )
                    }
                  />
                  Intern overføring
                </label>
              </div>
              {internalTransfer && (
                <div>
                  <Field
                    label="Til portefølje"
                    error={errMsg(errors.dpWithdrawalTargetPortfolioId)}
                  >
                    <div className="relative min-w-0">
                      <select
                        {...register("dpWithdrawalTargetPortfolioId")}
                        className={cn(
                          inputCls,
                          "min-w-0 appearance-none truncate pr-8"
                        )}
                        aria-label="Til portefølje"
                      >
                        <option value="">— velg mottakerportefølje —</option>
                        {targetPortfolios.map((portfolio) => (
                          <option
                            key={portfolio.id}
                            value={String(portfolio.id)}
                          >
                            {portfolio.name} ({portfolio.shortName}) ·{" "}
                            {portfolio.modelName}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        aria-hidden="true"
                        className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                      />
                    </div>
                    {targetPortfolios.length === 0 && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Intern overføring krever en annen Capguard-portefølje
                        hos samme kunde.
                      </p>
                    )}
                  </Field>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Manual orders section (NOMODEL only) ──────────────────────────────────────

function IpsSettingsSection() {
  const {
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const annualSavings = useFormattedAmount("dpIpsAnnualSavings");
  const withdrawalAge = useWatch({
    name: "dpIpsWithdrawalAge",
  }) as number | undefined;

  return (
    <div>
      <SubHeading title="IPS-innstillinger" />
      <p className="-mt-1 mb-3 text-[11px] text-muted-foreground">
        Uttak kan tidligst starte ved 62 år. Maksimalt årlig sparebeløp er NOK
        25 000.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="IPS Uttaksalder"
          error={errMsg(errors.dpIpsWithdrawalAge)}
        >
          <input
            type="number"
            min={62}
            step={1}
            value={withdrawalAge ?? ""}
            onChange={(event) =>
              setValue(
                "dpIpsWithdrawalAge",
                event.target.value === ""
                  ? undefined
                  : Number(event.target.value),
                { shouldDirty: true, shouldValidate: true }
              )
            }
            className={cn(
              inputCls,
              errors.dpIpsWithdrawalAge &&
                "border-destructive focus:border-destructive focus:ring-destructive/10"
            )}
          />
        </Field>
        <Field
          label="IPS Årlig sparebeløp"
          error={errMsg(errors.dpIpsAnnualSavings)}
        >
          <div className="relative">
            <input
              {...annualSavings.fieldProps}
              className={cn(
                inputCls,
                "pl-10",
                errors.dpIpsAnnualSavings &&
                  "border-destructive focus:border-destructive focus:ring-destructive/10"
              )}
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium leading-none text-muted-foreground">
              NOK
            </span>
          </div>
        </Field>
      </div>
    </div>
  );
}

function SecurityAutocomplete({
  value,
  securityId,
  securities,
  onSelect,
  onClear,
}: {
  value: string;
  securityId: number | null;
  securities: QuerySecurity[];
  onSelect: (security: QuerySecurity) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 260),
      zIndex: 9999,
    });
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !inputRef.current?.contains(e.target as Node) &&
        !listRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery(value);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, value]);

  const normalized = query.trim().toLowerCase();
  const filtered = securities
    .filter(
      (s) =>
        !normalized ||
        s.name?.toLowerCase().includes(normalized) ||
        s.security_code?.toLowerCase().includes(normalized) ||
        s.isin_code?.toLowerCase().includes(normalized)
    )
    .slice(0, 20);

  const selectedSecurity =
    securityId != null
      ? securities.find((s) => s.id === securityId)
      : undefined;
  const lastPrice = selectedSecurity?.unitPrice;
  const lastPriceCcy = selectedSecurity?.ccy;
  const lastPriceDate = selectedSecurity?.unitPriceDate
    ? dayjs(selectedSecurity.unitPriceDate).format("DD.MM.YYYY")
    : undefined;

  return (
    <div className="relative min-w-[160px] flex-1">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (securityId != null) onClear();
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Søk fond/instrument"
        className={cn(
          "w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20",
          lastPrice ? "pr-32" : undefined
        )}
      />
      {lastPrice ? (
        <MuiTooltip
          arrow
          placement="top"
          title={
            lastPriceDate
              ? `Sist kjente kurs ${lastPriceDate}`
              : "Sist kjente kurs"
          }
        >
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
            {formatNumber(lastPrice, 2)}
            {lastPriceCcy ? ` ${lastPriceCcy}` : ""}
          </span>
        </MuiTooltip>
      ) : null}
      {open &&
        createPortal(
          <ul
            ref={listRef}
            style={dropdownStyle}
            className="max-h-52 overflow-auto rounded-md border border-border bg-popover text-xs shadow-xl"
          >
            {filtered.length > 0 ? (
              filtered.map((s) => (
                <li
                  key={s.id}
                  onMouseDown={() => {
                    setQuery(s.name);
                    onSelect(s);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-baseline justify-between gap-2 px-3 py-1.5 hover:bg-primary/10"
                >
                  <span className="truncate font-medium">{s.name}</span>
                  <span className="shrink-0 font-mono text-muted-foreground">
                    {s.security_code}
                  </span>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-muted-foreground">
                {securities.length === 0
                  ? "Ingen instrumenter tilgjengelig"
                  : `Ingen treff for «${query}»`}
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
}

// Formats with thousands separators while blurred; shows raw digits + select-all on focus.
function BuyAmountInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  const numeric = parseFloat(value) || 0;
  const display = focused ? value : numeric ? fmt(numeric) : "";

  return (
    <div className="relative w-28 shrink-0">
      <input
        type="text"
        inputMode="numeric"
        placeholder="Beløp"
        value={display}
        onFocus={(e) => {
          setFocused(true);
          const el = e.target;
          requestAnimationFrame(() => el.select());
        }}
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))}
        onBlur={() => setFocused(false)}
        className="w-full rounded-md border border-border bg-background py-1.5 pl-2 pr-4 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">
        NOK
      </span>
    </div>
  );
}

// Duration is stored as a number of days; older rows may hold a date, which is ignored.
function LimitOrderInput({
  kind,
  value,
  onChange,
  label,
  className = "w-full",
}: {
  kind: "price" | "duration";
  value: string;
  onChange: (value: string) => void;
  label: string;
  className?: string;
}) {
  const isDuration = kind === "duration";
  return (
    <input
      type="text"
      inputMode={isDuration ? "numeric" : "decimal"}
      placeholder={isDuration ? "Varighet" : "Limitpris"}
      aria-label={label}
      max={isDuration ? 30 : undefined}
      min={isDuration ? 1 : undefined}
      value={isDuration && !/^\d+$/.test(value) ? "" : value}
      onChange={(event) =>
        onChange(
          isDuration
            ? event.target.value.replace(/\D/g, "")
            : event.target.value
        )
      }
      className={cn(
        "min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20",
        className
      )}
    />
  );
}

function LimitFields({
  price,
  days,
  onPrice,
  onDays,
  priceLabel = "Limitpris",
  daysLabel = "Varighet i dager",
}: {
  price: string;
  days: string;
  onPrice: (value: string) => void;
  onDays: (value: string) => void;
  priceLabel?: string;
  daysLabel?: string;
}) {
  return (
    <>
      <LimitOrderInput
        kind="price"
        label={priceLabel}
        value={price}
        onChange={onPrice}
        className="w-20"
      />
      <LimitOrderInput
        kind="duration"
        label={daysLabel}
        value={days}
        onChange={onDays}
        className="w-16"
      />
    </>
  );
}

function OptionCheckbox({
  id,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onChange(value === true)}
        className="mt-0.5"
      />
      <label
        htmlFor={id}
        className={cn("min-w-0", disabled && "cursor-not-allowed")}
      >
        <span
          className={cn(
            "block text-[12px] font-semibold text-foreground",
            disabled && "opacity-70"
          )}
        >
          {label}
        </span>
        <span className="block text-[11px] text-muted-foreground">{hint}</span>
      </label>
    </div>
  );
}

function ManualOrdersSection({
  selectedPortfolio,
  securities,
}: {
  selectedPortfolio?: Portfolio;
  securities: QuerySecurity[];
}) {
  const {
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const { rows: buyRows, update: updateBuy } = useBuyOrders();
  const { rows: sellRows, update: updateSell } = useSellOrders();

  const holdings = useMemo(
    () =>
      (selectedPortfolio?.portfolioReport?.portfolioReportItems ?? []).filter(
        (item) => !item.security.isAccount && item.amount >= 0
      ),
    [selectedPortfolio]
  );

  const sellRowsRef = useRef(sellRows);
  sellRowsRef.current = sellRows;

  useEffect(() => {
    const fresh = holdings.map(makeSellRowFromHolding);
    updateSell(mergeSellRows(fresh, sellRowsRef.current));
    // Only re-sync when the underlying holdings actually change, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings]);

  const totalBuy = buyRows.reduce(
    (s, r) => s + (parseFloat(r.amountInput) || 0),
    0
  );
  const sellEstimate = (r: SellOrderRow) =>
    (parseFloat(r.sellQty.replace(",", ".")) || 0) * (r.marketUnitPrice || 0);
  const totalSell = sellRows.reduce((s, r) => s + sellEstimate(r), 0);
  const withdrawal = (useWatch({ name: "dpWithdrawal" }) as number) || 0;
  const availableCash = (useWatch({ name: "dpSumAvCash" }) as number) || 0;
  const dynamicSell = !!(useWatch({ name: "portDynamicSell" }) as boolean);
  const dynamicWd = !!(useWatch({ name: "portDynamicWd" }) as boolean);
  const oneOffAmount = (useWatch({ name: "dpOnOffAmount" }) as number) || 0;

  // Manual buy orders must cover a one-off deposit.
  const buyShortfall = oneOffAmount > 0 ? oneOffAmount - totalBuy : 0;
  const hasBuyShortfall = buyShortfall > 0.005;
  const buyOrdersError = hasBuyShortfall
    ? undefined
    : errMsg(errors.buyOrders as { message?: unknown } | undefined);

  const swapDiff = Math.abs(totalSell - totalBuy);
  const canFundSwap =
    totalSell > 0 && totalBuy > 0 && swapDiff < FUND_SWAP_TOLERANCE_NOK;
  const sellPlusCash = totalSell + availableCash;
  const canWithdraw = withdrawal > 0 && sellPlusCash > withdrawal;

  // A checked option must not outlive the condition it depends on.
  useEffect(() => {
    if (dynamicSell && !canFundSwap)
      setValue("portDynamicSell", false, { shouldDirty: true });
  }, [dynamicSell, canFundSwap, setValue]);
  useEffect(() => {
    if (dynamicWd && !canWithdraw)
      setValue("portDynamicWd", false, { shouldDirty: true });
  }, [dynamicWd, canWithdraw, setValue]);

  const fundSwapHint =
    totalSell > 0 && totalBuy > 0
      ? `Estimert differanse mellom salg og kjøp: ${fmt(
          swapDiff
        )} NOK (må være under ${FUND_SWAP_TOLERANCE_NOK} NOK)`
      : `Krever salg og kjøp med differanse under ${FUND_SWAP_TOLERANCE_NOK} NOK (estimert)`;
  const withdrawalHint =
    withdrawal <= 0
      ? "Angi et uttaksbeløp for å aktivere"
      : `Estimert salg + cash (${fmt(sellPlusCash)} NOK) ${
          canWithdraw ? "dekker" : "dekker ikke"
        } uttaket (${fmt(withdrawal)} NOK)`;

  const addBuyRow = () => updateBuy([...buyRows, makeEmptyBuyRow()]);
  const removeBuyRow = (id: string) =>
    updateBuy(buyRows.filter((r) => r.id !== id));
  const patchBuyRow = (id: string, patch: Partial<BuyOrderRow>) =>
    updateBuy(buyRows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const selectBuySecurity = (id: string, security: QuerySecurity) => {
    const row = buyRows.find((r) => r.id === id);
    if (!row) return;

    const isFund = ["FI", "EF"].includes(security.class2code ?? "");
    patchBuyRow(id, {
      securityId: security.id,
      instrumentCode: security.security_code,
      instrumentName: security.name,
      isinCode: security.isin_code ?? "",
      class1code: security.class1code ?? "",
      class2id: security.class2id ?? "",
      class2code: security.class2code ?? "",
      price: String(security.unitPrice ?? ""),
      marketUnitPrice: security.unitPrice ?? 0,
      marketUnitPriceDate: security.unitPriceDate,
      type: isFund ? "" : row.type || "Market",
    });
  };

  const clearBuySecurity = (id: string) =>
    patchBuyRow(id, {
      securityId: null,
      instrumentCode: "",
      instrumentName: "",
      isinCode: "",
      class2code: "",
    });

  const sellAll = () => {
    const rows = mergeSellRows(
      holdings.map(makeSellRowFromHolding),
      sellRows
    ).map((row) => ({
      ...row,
      sellQty: row.holding > 0 ? String(row.holding) : "",
    }));
    updateSell(rows);
  };
  const patchSellRow = (securityId: number, patch: Partial<SellOrderRow>) =>
    updateSell(
      sellRows.map((r) =>
        r.securityId === securityId ? { ...r, ...patch } : r
      )
    );

  return (
    <div>
      <SubHeading title="Ordredetaljer (manuell)" />
      <div className="space-y-4">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
              Kjøp{totalBuy > 0 && ` · NOK ${fmt(totalBuy)}`}
            </span>
            <button
              type="button"
              onClick={addBuyRow}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-400"
            >
              <Plus className="size-3" /> Legg til
            </button>
          </div>
          {buyRows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[11px] text-muted-foreground">
              Ingen kjøpsordre lagt til.
            </p>
          ) : (
            <div className="space-y-1.5">
              {buyRows.map((row) => (
                <div
                  key={row.id}
                  className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border/70 bg-background/60 p-1.5"
                >
                  <SecurityAutocomplete
                    value={row.instrumentName}
                    securityId={row.securityId}
                    securities={securities}
                    onSelect={(security) => selectBuySecurity(row.id, security)}
                    onClear={() => clearBuySecurity(row.id)}
                  />
                  <BuyAmountInput
                    value={row.amountInput}
                    onChange={(value) =>
                      patchBuyRow(row.id, { amountInput: value })
                    }
                  />
                  <select
                    value={
                      isFundClass(row.class2code) ? "" : row.type || "Market"
                    }
                    disabled={isFundClass(row.class2code)}
                    onChange={(e) =>
                      patchBuyRow(row.id, {
                        type: e.target.value as BuyOrderRow["type"],
                      })
                    }
                    className="min-w-[5rem] rounded-md border border-border bg-background py-1.5 pl-2.5 pr-8 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isFundClass(row.class2code) && <option value="" />}
                    <option value="Market">Marked</option>
                    <option value="Limit">Limit</option>
                  </select>
                  {row.type === "Limit" && (
                    <LimitFields
                      price={row.limitPrice}
                      days={row.duration}
                      onPrice={(limitPrice) =>
                        patchBuyRow(row.id, { limitPrice })
                      }
                      onDays={(duration) => patchBuyRow(row.id, { duration })}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeBuyRow(row.id)}
                    aria-label="Fjern rad"
                    className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {hasBuyShortfall && (
            <div
              role="alert"
              className="mt-1.5 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-950/30"
            >
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
              <p className="text-[11px] text-amber-800 dark:text-amber-200">
                Engangstrekket er{" "}
                <span className="font-semibold">{fmt(oneOffAmount)} NOK</span>.
                Kjøpsordre må til sammen være minst like mye – mangler{" "}
                <span className="font-semibold">{fmt(buyShortfall)} NOK</span>.
              </p>
            </div>
          )}
          {buyOrdersError && (
            <p
              role="alert"
              className="mt-1.5 text-[11px] font-medium text-destructive"
            >
              {buyOrdersError}
            </p>
          )}
        </div>

        <SellHoldingsTable
          rows={sellRows}
          holdings={holdings}
          onPatch={patchSellRow}
          onReset={() => updateSell(holdings.map(makeSellRowFromHolding))}
          onSellAll={sellAll}
        />

        <div className="flex flex-col gap-3 border-t border-border/60 pt-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-2.5">
            <OptionCheckbox
              id="of-dynamic-sell"
              label="Fondsbytte"
              hint={fundSwapHint}
              checked={dynamicSell}
              disabled={!canFundSwap}
              onChange={(value) =>
                setValue("portDynamicSell", value, { shouldDirty: true })
              }
            />
            <OptionCheckbox
              id="of-dynamic-wd"
              label="Uttaksordre"
              hint={withdrawalHint}
              checked={dynamicWd}
              disabled={!canWithdraw}
              onChange={(value) =>
                setValue("portDynamicWd", value, { shouldDirty: true })
              }
            />
          </div>

          <dl className="w-full shrink-0 space-y-1 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-[11px] sm:w-56">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Salg (est.)</dt>
              <dd className="font-medium tabular-nums text-foreground">
                {fmt(totalSell)} NOK
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Kjøp</dt>
              <dd className="font-medium tabular-nums text-foreground">
                {fmt(totalBuy)} NOK
              </dd>
            </div>
            <div
              title="Salg minus kjøp"
              className="flex items-baseline justify-between gap-3 border-t border-border/60 pt-1"
            >
              <dt className="font-semibold text-foreground">Totalt</dt>
              <dd
                className={cn(
                  "font-semibold tabular-nums",
                  canFundSwap
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-foreground"
                )}
              >
                {totalSell - totalBuy > 0
                  ? "+"
                  : totalSell - totalBuy < 0
                  ? "−"
                  : ""}
                {fmt(swapDiff)} NOK
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

// ─── Agreements section (savings + withdrawal) ─────────────────────────────────

function AgreementsSection({
  securities,
  accountNumbers,
}: {
  securities: QuerySecurity[];
  accountNumbers: string[];
}) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const modelType = useWatch({ name: "dpModelType" }) as string | undefined;
  const savingsEnabled = useWatch({ name: "dpSavingsEnabled" }) as boolean;
  const withdrawalEnabled = useWatch({
    name: "dpWithdrawalEnabled",
  }) as boolean;
  const savingsAvtalegiro = useWatch({
    name: "dpSavingsAvtalegiro",
  }) as boolean;
  const bankAccount = useWatch({ name: "dpAccount" }) as string;
  const newAccount = useWatch({ name: "dpNewAccount" }) as string;
  const newAccountOption =
    bankAccount === "newAccount" &&
    normalizeAccountNumber(newAccount || "").length === 11
      ? normalizeAccountNumber(newAccount)
      : "";
  const hasModel = !!modelType && modelType !== "";
  const isNoModel = !modelType || modelType === "NOMODEL";

  const {
    fields: fundFields,
    append: appendFund,
    remove: removeFund,
    update: updateFund,
  } = useFieldArray<SavingsFundsFieldValues, "dpSavingsFunds">({
    control: control as unknown as Control<SavingsFundsFieldValues>,
    name: "dpSavingsFunds",
  });

  const fundSumPct = fundFields.reduce((sum, f) => sum + (f.pct || 0), 0);

  useEffect(() => {
    // A model portfolio invests savings automatically — manual fund picks don't apply
    if (!isNoModel) setValue("dpSavingsFunds", []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNoModel]);

  const toggleSavings = (next: boolean) => {
    setValue("dpSavingsEnabled", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!next) {
      setValue("dpSavingsAmount", undefined);
      setValue("dpSavingsDayOfMonth", undefined);
      setValue("dpSavingsAvtalegiro", false);
      setValue("dpSavingsFunds", []);
    }
  };
  const toggleWithdrawal = (next: boolean) => {
    setValue("dpWithdrawalEnabled", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!next) {
      setValue("dpWdAccount", "");
      setValue("dpWdDayOfMonth", undefined);
      setValue("dpWdAmount", undefined);
    }
  };

  return (
    <div>
      <SubHeading title="Avtaler" />
      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleCard
          icon={PiggyBank}
          label="Spareavtale"
          hint="Faste, periodiske innskudd i modellen"
          checked={savingsEnabled}
          disabled={!hasModel}
          onChange={toggleSavings}
        />
        <ToggleCard
          icon={Landmark}
          label="Uttaksavtale"
          hint="Faste, periodiske uttak fra kontoen"
          checked={withdrawalEnabled}
          disabled={!hasModel}
          onChange={toggleWithdrawal}
        />
      </div>

      {savingsEnabled && (
        <div className="mt-3 space-y-3 rounded-lg border border-emerald-200/60 bg-emerald-50/40 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/10">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <PiggyBank aria-hidden="true" className="size-3.5" />
            Spareavtale
          </h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <AmountField
              name="dpSavingsAmount"
              label="Beløp per periode"
              error={errMsg(errors.dpSavingsAmount)}
            />
            <Field label="Trekkdato" error={errMsg(errors.dpSavingsDayOfMonth)}>
              <select
                {...register("dpSavingsDayOfMonth", { valueAsNumber: true })}
                className={inputCls}
              >
                <option value="">Velg dag</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    {day}.
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-xs text-foreground">
                <Switch
                  size="sm"
                  checked={savingsAvtalegiro}
                  onCheckedChange={(checked) =>
                    setValue("dpSavingsAvtalegiro", checked, {
                      shouldDirty: true,
                    })
                  }
                />
                AvtaleGiro
              </label>
            </div>
          </div>

          {isNoModel && (
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-foreground/80">
                  Fondsfordeling
                </span>
                <span
                  className={cn(
                    "text-[10px] font-semibold",
                    fundSumPct > 100
                      ? "text-destructive"
                      : "text-muted-foreground"
                  )}
                >
                  Sum {fundSumPct}%
                </span>
              </div>
              <div className="space-y-1.5">
                {fundFields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-1.5">
                    <input
                      type="text"
                      list="of-funds"
                      placeholder="Fond"
                      value={field.name}
                      onChange={(e) =>
                        updateFund(index, {
                          name: e.target.value,
                          pct: field.pct,
                        })
                      }
                      className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={field.pct}
                      onChange={(e) =>
                        updateFund(index, {
                          name: field.name,
                          pct: Number(e.target.value) || 0,
                        })
                      }
                      className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    />
                    <span className="text-[10px] text-muted-foreground">%</span>
                    <button
                      type="button"
                      onClick={() => removeFund(index)}
                      aria-label="Fjern fond"
                      className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
                {fundFields.length < MAX_SAVINGS_FUNDS && (
                  <button
                    type="button"
                    onClick={() => appendFund({ name: "", pct: 0 })}
                    className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-400"
                  >
                    <Plus className="size-3" /> Legg til fond
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {withdrawalEnabled && (
        <div className="mt-3 grid gap-3 rounded-lg border border-blue-200/60 bg-blue-50/40 p-3 dark:border-blue-900/50 dark:bg-blue-950/10 sm:grid-cols-3">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground sm:col-span-3">
            <Landmark aria-hidden="true" className="size-3.5" />
            Uttaksavtale
          </h3>
          <Field label="Konto" error={errMsg(errors.dpWdAccount)}>
            <select {...register("dpWdAccount")} className={inputCls}>
              <option value="">— velg konto —</option>
              {newAccountOption && (
                <option value={newAccountOption}>
                  {formatAccountNumber(newAccountOption)} (ny konto)
                </option>
              )}
              {accountNumbers.map((n) => (
                <option key={n} value={n}>
                  {formatAccountNumber(n)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Uttaksdato" error={errMsg(errors.dpWdDayOfMonth)}>
            <select
              {...register("dpWdDayOfMonth", { valueAsNumber: true })}
              className={inputCls}
            >
              <option value="">Velg dag</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  {day}.
                </option>
              ))}
            </select>
          </Field>
          <AmountField
            name="dpWdAmount"
            label="Beløp per periode"
            error={errMsg(errors.dpWdAmount)}
          />
        </div>
      )}
      <datalist id="of-funds">
        {securities
          .filter((s) => ["FI", "EF"].includes(s.class2code))
          .slice(0, 300)
          .map((s) => (
            <option key={s.id} value={s.name} />
          ))}
      </datalist>
    </div>
  );
}

// ─── Transfer authorization section (ASK / IPS / VPS) ─────────────────────────

function TransferSection({ securities }: { securities: QuerySecurity[] }) {
  const { register, setValue } = useFormContext<OrderFormData>();
  const moveAll = useWatch({ name: "moveAll" }) as boolean;
  const instrumentValues = useWatch({
    name: Array.from(
      { length: MAX_INSTRUMENT_ROWS },
      (_, index) => `Instrument${index + 1}` as Path<OrderFormData>
    ),
  }) as Array<string | undefined>;
  const [selectedSecurityIds, setSelectedSecurityIds] = useState<
    Record<number, number | null>
  >({});

  const activeRowCount = instrumentValues.reduce(
    (last, value, index) => (value ? index + 1 : last),
    0
  );
  const [rowCount, setRowCount] = useState(activeRowCount);

  useEffect(() => {
    if (activeRowCount > rowCount) setRowCount(activeRowCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRowCount]);

  const addRow = () =>
    setRowCount((count) => Math.min(MAX_INSTRUMENT_ROWS, count + 1));
  const removeRow = (row: number) => {
    setValue(fieldPath(`Instrument${row}`), "", { shouldDirty: true });
    setValue(fieldPath(`InstrumentAmt${row}`), undefined, {
      shouldDirty: true,
    });
    setSelectedSecurityIds((current) => {
      const next = { ...current };
      delete next[row];
      return next;
    });
    setRowCount((count) => Math.max(0, count - 1));
  };

  return (
    <div>
      <SubHeading title="Flyttefullmakt" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Avleverende tilbyder">
          <input
            type="text"
            {...register("moveFrom")}
            placeholder="F.eks. Nordnet"
            className={inputCls}
          />
        </Field>
        <Field label="KontoID / kontonummer">
          <input
            type="text"
            {...register("moveFromId")}
            placeholder="KontoID eller kontonummer"
            className={inputCls}
          />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <ToggleCard
          icon={ArrowRightLeft}
          label="Flytt hele beholdningen"
          hint="Ingen enkeltinstrumenter nødvendig"
          checked={moveAll}
          onChange={(next) => setValue("moveAll", next, { shouldDirty: true })}
        />
      </div>

      {!moveAll && (
        <div className="mt-3 space-y-1.5">
          {Array.from({ length: rowCount }, (_, index) => {
            const row = index + 1;
            return (
              <div key={row} className="flex items-center gap-1.5">
                <SecurityAutocomplete
                  value={instrumentValues[row - 1] ?? ""}
                  securityId={selectedSecurityIds[row] ?? null}
                  securities={securities}
                  onSelect={(security) => {
                    setValue(fieldPath(`Instrument${row}`), security.name, {
                      shouldDirty: true,
                    });
                    setSelectedSecurityIds((current) => ({
                      ...current,
                      [row]: security.id,
                    }));
                  }}
                  onClear={() => {
                    setValue(fieldPath(`Instrument${row}`), "", {
                      shouldDirty: true,
                    });
                    setSelectedSecurityIds((current) => ({
                      ...current,
                      [row]: null,
                    }));
                  }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  {...register(fieldPath(`InstrumentAmt${row}`), {
                    valueAsNumber: true,
                  })}
                  placeholder="Antall"
                  className="w-28 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                />
                <button
                  type="button"
                  onClick={() => removeRow(row)}
                  aria-label="Fjern instrument"
                  className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            );
          })}
          {rowCount < MAX_INSTRUMENT_ROWS && (
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Plus className="size-3.5" /> Legg til instrument
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sidebar: summary, projection & price list ─────────────────────────────────

function SummaryCard({
  portfolios,
  targetPortfolios,
  modelOptions,
  onChangeServiceType,
}: {
  portfolios: Portfolio[];
  targetPortfolios: InternalTransferTarget[];
  modelOptions: { value: string; label: string }[];
  onChangeServiceType: () => void;
}) {
  const serviceType = useWatch({ name: "dpServiceType" }) as string | undefined;
  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const portType = useWatch({ name: "dpPortType" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) || 0;
  const subscriptionFeePct = useWatch({ name: "dpOnOffPercentage" }) as string;
  const managementFee = useWatch({ name: "dpManagementFee" }) as string;
  const bankAccount = useWatch({ name: "dpAccount" }) as string;
  const newAccount = useWatch({ name: "dpNewAccount" }) as string;
  const newAccountIsValid =
    bankAccount === "newAccount" && validateAccountNumber(newAccount || "");

  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));

  const oneOffAmount = (useWatch({ name: "dpOnOffAmount" }) as number) || 0;
  const withdrawal = (useWatch({ name: "dpWithdrawal" }) as number) || 0;
  const withdrawalInternalTransfer = useWatch<
    OrderFormData,
    "dpWithdrawalInternalTransfer"
  >({
    name: "dpWithdrawalInternalTransfer",
  });
  const withdrawalTargetPortfolioId = useWatch<
    OrderFormData,
    "dpWithdrawalTargetPortfolioId"
  >({
    name: "dpWithdrawalTargetPortfolioId",
  });
  const savingsEnabled = useWatch({ name: "dpSavingsEnabled" }) as boolean;
  const savingsAmount = (useWatch({ name: "dpSavingsAmount" }) as number) || 0;
  const withdrawalEnabled = useWatch({
    name: "dpWithdrawalEnabled",
  }) as boolean;
  const wdAmount = (useWatch({ name: "dpWdAmount" }) as number) || 0;
  const buyCount = (
    (useWatch({ name: "buyOrders" }) as BuyOrderRow[] | null) ?? []
  ).filter((r) => r.instrumentName).length;
  const sellCount = (
    (useWatch({ name: "sellOrders" }) as SellOrderRow[] | null) ?? []
  ).filter((r) => (parseFloat(r.sellQty) || 0) > 0).length;

  const rows: { label: string; value: string; verified?: boolean }[] = [
    {
      label: "Tjeneste",
      value: (serviceType && SERVICE_TYPE_LABELS[serviceType]) || "—",
    },
    {
      label: "Type",
      value:
        portfolioTypeOptions.find((o) => o.value === portType)?.label || "—",
    },
    {
      label: "Modell",
      value: modelOptions.find((o) => o.value === modelType)?.label || "—",
    },
    { label: "Tegningsgebyr", value: `${subscriptionFeePct || "0"} %` },
    {
      label: "Forvaltning",
      value:
        managementFee === "1.5" ? "Listepris" : `${managementFee || "—"} %`,
    },
    {
      label: "Bankkonto",
      value:
        bankAccount === "newAccount"
          ? formatAccountNumber(newAccount) || "—"
          : formatAccountNumber(bankAccount) || "—",
      verified: newAccountIsValid,
    },
  ];
  if (oneOffAmount > 0)
    rows.push({ label: "Engangstrekk", value: `NOK ${fmt(oneOffAmount)}` });
  if (withdrawal > 0)
    rows.push({ label: "Uttak", value: `NOK ${fmt(withdrawal)}` });
  if (withdrawal > 0 && withdrawalInternalTransfer)
    rows.push({
      label: "Intern overføring til",
      value:
        targetPortfolios.find(
          (portfolio) => portfolio.id === withdrawalTargetPortfolioId
        )?.name || "Velg portefølje",
    });
  if (savingsEnabled)
    rows.push({
      label: "Spareavtale",
      value: savingsAmount ? `NOK ${fmt(savingsAmount)}` : "Aktiv",
    });
  if (withdrawalEnabled)
    rows.push({
      label: "Uttaksavtale",
      value: wdAmount ? `NOK ${fmt(wdAmount)}` : "Aktiv",
    });
  if (buyCount > 0) rows.push({ label: "Kjøpsordre", value: String(buyCount) });
  if (sellCount > 0)
    rows.push({ label: "Salgsordre", value: String(sellCount) });

  return (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-card">
      <div className="border-b border-border/50 bg-primary/[0.04] p-3">
        <div className="flex items-center gap-2">
          <Wallet className="size-3.5 text-primary" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Sammendrag
          </span>
        </div>
        <p className="mt-2 truncate text-[13px] font-semibold text-foreground">
          {selected?.name || (portfolioId === "NEW" ? "Ny portefølje" : "—")}
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          Beløp å investere
        </p>
        <p className="text-xl font-bold tabular-nums tracking-tight text-primary">
          NOK {fmt(amountToInvest)}
        </p>
      </div>
      <div className="space-y-1.5 p-3">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3 text-[11px]"
          >
            <span className="max-w-[45%] shrink-0 text-muted-foreground">
              {row.label}
            </span>
            <span className="flex min-w-0 max-w-[60%] items-center justify-end gap-1 text-right font-medium tabular-nums text-foreground">
              <span className="min-w-0 truncate" title={row.value}>
                {row.value}
              </span>
              {row.label === "Tjeneste" && (
                <button
                  type="button"
                  onClick={onChangeServiceType}
                  className="ml-1 shrink-0 text-[10px] font-semibold text-primary hover:underline"
                >
                  Endre
                </button>
              )}
              {row.verified && (
                <MuiTooltip arrow placement="top" title={VALID_ACCOUNT_TOOLTIP}>
                  <span className="inline-flex shrink-0">
                    <Check
                      aria-label="Verifisert kontonummer"
                      className="size-3 text-emerald-600 dark:text-emerald-400"
                    />
                  </span>
                </MuiTooltip>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

type AfterHolding = {
  key: string;
  name: string;
  marketValue: number;
  isEquity: boolean;
};

function useProjection(
  portfolios: Portfolio[],
  modelsData: QueryModelPosition[]
) {
  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const modelTypeName = useWatch({ name: "dpModelTypeName" }) as string;
  const rawBuyOrders = useWatch({ name: "buyOrders" }) as BuyOrderRow[] | null;
  const rawSellOrders = useWatch({ name: "sellOrders" }) as
    | SellOrderRow[]
    | null;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) ?? 0;
  const withdrawal = (useWatch({ name: "dpWithdrawal" }) as number) ?? 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) ?? 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) ?? 0;

  const buyOrders = useMemo(() => rawBuyOrders ?? [], [rawBuyOrders]);
  const sellOrders = useMemo(() => rawSellOrders ?? [], [rawSellOrders]);

  const isNew = portfolioId === "NEW";
  const isNoModel = !modelType || modelType === "NOMODEL";
  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));

  const currentItems = useMemo(
    () => selected?.portfolioReport?.portfolioReportItems ?? [],
    [selected]
  );
  const currentCash = selected?.portfolioReport?.accountBalance ?? 0;
  const currentMv = selected?.portfolioReport?.marketValue ?? 0;
  const currentEquityMv = currentItems
    .filter((i) => isEquityByClass1(i.security?.classType1?.code ?? ""))
    .reduce((s, i) => s + (i.marketValue ?? 0), 0);
  const currentEquityPct =
    currentMv > 0 ? (currentEquityMv / currentMv) * 100 : 0;

  const modelEquityFraction = useMemo(() => {
    if (isNoModel) return null;
    const positions = modelsData.filter((p) => p.short_name === modelType);
    if (positions.length > 0) {
      const total = positions.reduce((s, p) => s + p.share_of_portfolio, 0);
      const stockTotal = positions
        .filter((p) => isEquityByModelClass1(p.class_type1))
        .reduce((s, p) => s + p.share_of_portfolio, 0);
      return total > 0 ? stockTotal / total : 0;
    }
    return getModelEquityFractionFallback(modelType);
  }, [modelType, isNoModel, modelsData]);

  const modelDisplayName = useMemo(() => {
    if (!modelType || isNoModel) return null;
    return MODEL_DISPLAY_NAMES[modelType] ?? modelTypeName ?? modelType;
  }, [modelType, modelTypeName, isNoModel]);

  const afterTotalModel = isNew
    ? amountToInvest
    : Math.max(0, sumInsValue + sumAvCash + amountToInvest - withdrawal);
  const afterEquityPctModel =
    modelEquityFraction != null ? modelEquityFraction * 100 : 0;

  const { afterHoldings, afterCash } = useMemo(() => {
    if (!isNoModel)
      return { afterHoldings: [] as AfterHolding[], afterCash: 0 };
    const map = new Map<string, AfterHolding>();
    for (const item of currentItems) {
      const key = String(item.security.id);
      map.set(key, {
        key,
        name: item.security.name,
        marketValue: item.marketValue ?? 0,
        isEquity: isEquityByClass1(item.security?.classType1?.code ?? ""),
      });
    }
    for (const sell of sellOrders) {
      const qty = parseFloat(sell.sellQty) || 0;
      if (qty <= 0) continue;
      const key = String(sell.securityId);
      const existing = map.get(key);
      if (!existing) continue;
      const remaining = existing.marketValue - qty * sell.marketUnitPrice;
      if (remaining <= 1e-6) map.delete(key);
      else map.set(key, { ...existing, marketValue: remaining });
    }
    for (const buy of buyOrders) {
      if (!buy.instrumentName) continue;
      const amt = parseFloat(buy.amountInput) || 0;
      if (amt <= 0) continue;
      const key = buy.securityId
        ? String(buy.securityId)
        : `buy_${buy.instrumentName}`;
      const existing = map.get(key);
      if (existing)
        map.set(key, { ...existing, marketValue: existing.marketValue + amt });
      else
        map.set(key, {
          key,
          name: buy.instrumentName,
          marketValue: amt,
          isEquity: isEquityByClass1(buy.class1code),
        });
    }
    const totalBuyAmt = buyOrders.reduce(
      (s, r) => s + (parseFloat(r.amountInput) || 0),
      0
    );
    const totalSellAmt = sellOrders.reduce(
      (s, r) => s + (parseFloat(r.sellQty) || 0) * (r.marketUnitPrice ?? 0),
      0
    );
    const cash =
      currentCash + amountToInvest + totalSellAmt - totalBuyAmt - withdrawal;
    return {
      afterHoldings: Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      afterCash: cash,
    };
  }, [
    isNoModel,
    currentItems,
    currentCash,
    amountToInvest,
    withdrawal,
    buyOrders,
    sellOrders,
  ]);

  const afterSecMv = afterHoldings.reduce((s, h) => s + h.marketValue, 0);
  const afterEquityMv = afterHoldings
    .filter((h) => h.isEquity)
    .reduce((s, h) => s + h.marketValue, 0);
  const afterTotalNoModel = afterSecMv + Math.max(afterCash, 0);
  const afterEquityPctNoModel =
    afterTotalNoModel > 0 ? (afterEquityMv / afterTotalNoModel) * 100 : 0;

  const hasChanges =
    buyOrders.some((r) => r.instrumentName && parseFloat(r.amountInput) > 0) ||
    sellOrders.some((r) => parseFloat(r.sellQty) > 0) ||
    amountToInvest > 0 ||
    withdrawal > 0;

  return {
    selected,
    isNew,
    isNoModel,
    currentMv,
    currentEquityPct,
    afterTotal: isNoModel ? afterTotalNoModel : afterTotalModel,
    afterEquityPct: isNoModel ? afterEquityPctNoModel : afterEquityPctModel,
    afterEquityPctModel,
    modelDisplayName,
    modelEquityFraction,
    afterHoldings,
    afterCash,
    afterTotalNoModel,
    amountToInvest,
    hasChanges,
  };
}

function ProjectionCard({
  portfolios,
  modelsData,
}: {
  portfolios: Portfolio[];
  modelsData: QueryModelPosition[];
}) {
  const p = useProjection(portfolios, modelsData);

  if (!p.selected && !p.isNew) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border/70 bg-card p-6 text-center text-muted-foreground">
        <TrendingUp className="size-6 opacity-30" />
        <p className="text-[11px]">Velg en portefølje for å se projeksjon</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/70 bg-card p-3">
      <div className="mb-2 flex items-center gap-2 border-b border-border/50 pb-2">
        <TrendingUp className="size-3.5 text-primary" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Porteføljeprojeksjon
        </span>
      </div>

      {p.selected && (
        <div className="grid grid-cols-2 gap-2">
          <StatChip label="Før" value={fmt(p.currentMv)} />
          <StatChip
            label="Etter"
            value={fmt(p.afterTotal)}
            highlight={p.hasChanges}
          />
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
        <span>Aksjer {formatNumber(p.currentEquityPct, 1)}%</span>
        <span className={cn(p.hasChanges && "font-semibold text-primary")}>
          Aksjer {formatNumber(p.afterEquityPct, 1)}%
        </span>
      </div>

      {!p.isNoModel && p.modelEquityFraction != null && (
        <div className="mt-3 overflow-hidden rounded-lg border border-border/60">
          <div className="flex items-center justify-between border-b border-border/40 px-2.5 py-1.5">
            <span className="text-[11px] font-semibold text-foreground">
              {p.modelDisplayName}
            </span>
            {p.amountToInvest > 0 && (
              <span className="text-[10px] tabular-nums text-muted-foreground">
                + NOK {fmt(p.amountToInvest)}
              </span>
            )}
          </div>
          <div className="px-2.5 pt-2.5">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="bg-primary"
                style={{ width: `${p.afterEquityPctModel}%` }}
              />
              <div
                className="bg-emerald-400"
                style={{ width: `${100 - p.afterEquityPctModel}%` }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 px-2.5 py-2">
            <span className="text-[10px] text-muted-foreground">
              <span className="mr-1 inline-block size-1.5 rounded-full bg-primary" />
              Aksjer {formatNumber(p.afterEquityPctModel, 1)}%
            </span>
            <span className="text-[10px] text-muted-foreground">
              <span className="mr-1 inline-block size-1.5 rounded-full bg-emerald-400" />
              Renter {formatNumber(100 - p.afterEquityPctModel, 1)}%
            </span>
          </div>
        </div>
      )}

      {p.isNoModel && (p.afterHoldings.length > 0 || p.afterCash !== 0) && (
        <div className="mt-3 overflow-hidden rounded-lg border border-border/60">
          <div className="flex items-center justify-between border-b border-border/40 bg-muted/30 px-2.5 py-1.5">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
              Beholdning etter
            </span>
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {fmt(p.afterTotalNoModel)}
            </span>
          </div>
          <div className="max-h-56 divide-y divide-border/40 overflow-y-auto">
            {p.afterHoldings.map((h) => {
              const pct =
                p.afterTotalNoModel > 0
                  ? (h.marketValue / p.afterTotalNoModel) * 100
                  : 0;
              return (
                <div
                  key={h.key}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        h.isEquity ? "bg-primary" : "bg-emerald-400"
                      )}
                    />
                    <p className="truncate text-[11px]">{h.name}</p>
                  </div>
                  <div className="shrink-0 text-right text-[11px] tabular-nums">
                    {fmt(h.marketValue)}{" "}
                    <span className="text-[9px] text-muted-foreground">
                      {formatNumber(pct, 1)}%
                    </span>
                  </div>
                </div>
              );
            })}
            {p.afterCash !== 0 && (
              <div className="flex items-center justify-between px-2.5 py-1.5 text-[11px] text-muted-foreground">
                <span>Cash</span>
                <span
                  className={cn(
                    "tabular-nums",
                    p.afterCash < 0 && "text-destructive"
                  )}
                >
                  {fmt(p.afterCash)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PriceListCard({
  open,
  onToggle,
  totalPortValue,
  modelType,
  managementFee,
}: {
  open: boolean;
  onToggle: () => void;
  totalPortValue: number;
  modelType: string;
  managementFee: string;
}) {
  const mnok = totalPortValue / 1_000_000;
  const activeTier =
    mnok >= 10
      ? "10+"
      : mnok >= 5
      ? "5-10"
      : mnok >= 2
      ? "2-5"
      : totalPortValue > 0
      ? "<2"
      : null;

  const modelToKey: Record<
    string,
    "active" | "ekb" | "realEstate" | "bonds" | "safe"
  > = {
    CAPGUARD75: "active",
    CAPGUARD60: "active",
    CAPGUARD45: "active",
    CAPGUARD25: "active",
    CAPGUARDAKSJER: "active",
    CAPGUARDEKB: "ekb",
    CAPGUARDEIENDOM: "realEstate",
    CAPGUARDRENTER: "bonds",
    FONDSOBLIGASJON: "bonds",
    CAPGUARDTRYGG: "safe",
  };
  const activeProductKey = modelType ? modelToKey[modelType] ?? null : null;
  const products = [
    { label: "Aktiv", key: "active" as const },
    { label: "EKB", key: "ekb" as const },
    { label: "Eiendom", key: "realEstate" as const },
    { label: "Renter", key: "bonds" as const },
    { label: "Trygg", key: "safe" as const },
  ];
  const feeRows = PRICELIST_ROWS.filter(
    (r) =>
      r.tier !== "Kurtasje belastes" &&
      r.tier !== "Minstetegning" &&
      r.tier !== "Depot" &&
      r.tier !== "Inn/Ut"
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border",
        open ? "border-primary/40" : "border-border/70"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center justify-between px-3 py-2 transition-colors",
          open ? "bg-primary/10" : "hover:bg-muted/40"
        )}
      >
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          <BadgePercent className="size-3 text-primary" /> Prisliste
        </span>
        <ChevronDown
          className={cn(
            "size-3 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-border/40">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-muted/30">
                <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground" />
                {feeRows.map((row) => (
                  <th
                    key={row.tier}
                    className="px-2 py-1.5 text-right font-semibold tabular-nums text-muted-foreground"
                  >
                    {row.tier}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {products.map(({ label, key }) => (
                <tr key={label}>
                  <td className="whitespace-nowrap px-2 py-1.5 font-semibold text-foreground">
                    {label}
                  </td>
                  {feeRows.map((row) => {
                    const isActive =
                      managementFee === "1.5" &&
                      activeProductKey === key &&
                      row.tier === activeTier;
                    return (
                      <td
                        key={row.tier}
                        className={cn(
                          "px-2 py-1.5 text-right tabular-nums font-medium",
                          isActive
                            ? "rounded bg-primary text-primary-foreground"
                            : "text-foreground"
                        )}
                      >
                        {row[key]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type ProfileValueType = "double" | "integer" | "string" | "boolean";

type ProfileValueByType = {
  double: number;
  integer: number;
  string: string;
  boolean: boolean;
};

function getProfileValue<T extends ProfileValueType>(
  profile: QueryProfile[],
  key: string,
  type: T
): ProfileValueByType[T] {
  const item = profile.find((p) => p.attributeKey === key);
  switch (type) {
    case "double":
      return (item?.doubleValue ?? 0) as ProfileValueByType[T];
    case "integer":
      return (item?.intValue ?? 0) as ProfileValueByType[T];
    case "string":
      return (item?.stringValue ?? "") as ProfileValueByType[T];
    case "boolean":
      return (item?.booleanValue ?? false) as ProfileValueByType[T];
    default:
      return (item?.stringValue ?? "") as ProfileValueByType[T];
  }
}

// ─── buildExistData (matches the contract consumed by onDocumentCreated) ──────

function buildExistData(
  portfolio: Portfolio | undefined,
  profile: QueryProfile[]
) {
  if (!portfolio) return { isExisting: false };
  const managementFee = portfolio.fees?.find((f) => f.type === "Management");
  const karOkAccount = portfolio.accounts?.find((a) =>
    a.tagsAsSet.includes("KAR-OK")
  );

  const ipsWdAge = getProfileValue(
    profile,
    "portfolio.noTaxReporting.ipsWdAge",
    "integer"
  );
  const ipsSavingPlan = getProfileValue(
    profile,
    "portfolio.noTaxReporting.ipsSavingPlan",
    "double"
  );

  return {
    dpPortfolioId: String(portfolio.id),
    dpPortfolioName: portfolio.name,
    existingAdvancedEKB: getExistingAdvancedEKB(portfolio.tagsAsSet),
    dpPortfolioShortName: portfolio.shortName ?? "",
    dpPortType: portfolio.typeCode ?? "",
    dpModelType:
      portfolio.modelPortfolio?.linkedPortfolio?.shortName ??
      portfolio.modelPortfolio?.code ??
      "",
    dpModelTypeName:
      portfolio.modelPortfolio?.linkedPortfolio?.name ??
      portfolio.modelPortfolio?.name ??
      "",
    dpAmountToInvest: 0,
    dpSumAvCash: portfolio.portfolioReport?.accountBalance ?? 0,
    dpSumInsValue: portfolio.portfolioReport?.positionMarketValue ?? 0,
    dpOnOffPercentage: "0",
    dpManagementFee:
      managementFee?.feePercent !== undefined
        ? String(managementFee.feePercent)
        : "1.5",
    dpFeeType: "all",
    dpOnOffAmount: 0,
    dpOnOffDate: "",
    dpAccount: karOkAccount?.number ?? "",
    dpNewAccount: "",
    dpWithdrawal: 0,
    dpIpsWithdrawalAge: ipsWdAge,
    dpIpsAnnualSavings: ipsSavingPlan,
    ordreModul: "no",
    buyOrders: [],
    sellOrders: [],
    isExisting: true,
  };
}

function AdvancedEKBSection() {
  const { register, setValue } = useFormContext<OrderFormData>();

  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 text-[11px] font-medium leading-snug">
        Midler i min EKB-portefølje tilsvarende det aktuelle tegningsgarantibeløpet
        skal plasseres på følgende måte fra garantien avgis og inntil
        garantiansvaret er utløst eller bortfalt (velg ett alternativ)
      </legend>
      {ADVANCED_EKB_OPTIONS.map((option, index) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] leading-snug hover:bg-muted/40"
        >
          <input
            type="radio"
            {...register("advancedEKB")}
            value={option.value}
            className="mt-0.5 size-3 shrink-0 accent-primary"
          />
          <span>{index + 1}. {option.label}</span>
        </label>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
        <p>Valgfritt. Uten nytt valg beholdes eksisterende innstilling.</p>
        <button
          type="button"
          onClick={() => setValue("advancedEKB", "", { shouldDirty: true })}
          className="shrink-0 underline hover:text-foreground"
        >
          Nullstill valg
        </button>
      </div>
    </fieldset>
  );
}

// ─── Content (needs FormProvider context) ──────────────────────────────────────

function OrderFormCleanContent({
  portfolios,
  targetPortfolios,
  blockedPortfolioIds,
  editingPortfolioId,
  securities,
  isCompany,
  classification2Code,
  classification3Code,
  modelsData,
  profile,
  additionalAccountNumbers,
  onChangeServiceType,
}: {
  portfolios: Portfolio[];
  targetPortfolios: InternalTransferTarget[];
  blockedPortfolioIds: ReadonlySet<string>;
  editingPortfolioId?: string;
  securities: QuerySecurity[];
  isCompany: boolean;
  classification2Code?: string;
  classification3Code?: string;
  modelsData: QueryModelPosition[];
  profile: QueryProfile[];
  additionalAccountNumbers: string[];
  onChangeServiceType: () => void;
}) {
  const { setValue, clearErrors, formState } = useFormContext<OrderFormData>();

  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const portType = useWatch({ name: "dpPortType" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const managementFee = useWatch({ name: "dpManagementFee" }) as string;
  const bankAccount = useWatch({ name: "dpAccount" }) as string;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) || 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) || 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) || 0;
  const selectPortfolio = (id: string) =>
    setValue("dpPortfolioId", id, { shouldValidate: true, shouldDirty: true });

  const isNew = portfolioId === "NEW";
  const isNoModel = modelType === "NOMODEL";
  const modelIsSet = !!modelType && !isNoModel;
  const isCapguardPort = CAPGUARD_PORT_TYPES.includes(portType ?? "");
  const isAsk = portType === "22";
  const isIps = portType === "23";
  const isVps = portType === "21";
  const isTransferAccount = isAsk || isIps || isVps;
  const totalPortValue = sumAvCash + sumInsValue + amountToInvest;
  const minThreshold = getMinimumMandateAmount(modelType);
  const showMinWarn =
    isNew &&
    minThreshold > 0 &&
    totalPortValue > 0 &&
    totalPortValue < minThreshold;
  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));
  const prevPortfolioIdRef = useRef(portfolioId);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const ipsWdAge = getProfileValue(
    profile,
    "portfolio.noTaxReporting.ipsWdAge",
    "integer"
  );
  const ipsSavingPlan = getProfileValue(
    profile,
    "portfolio.noTaxReporting.ipsSavingPlan",
    "double"
  );

  const oneOff = useFormattedAmount("dpOnOffAmount");
  const copyAmountToOneOff = () => oneOff.apply(Math.max(0, amountToInvest));
  const scrollToTransferSection = () => {
    const container = scrollContainerRef.current;
    const target = document.getElementById("of-transfer-section");
    if (!container || !target) return;
    // Below xl the form column does not scroll itself; the outer wrapper does.
    if (container.scrollHeight <= container.clientHeight) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // Scroll only our own container — scrollIntoView can also scroll the
    // surrounding dialog, leaving a large empty gap below the content.
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = container.scrollTop + (targetRect.top - containerRect.top) - 12;
    container.scrollTo({ top, behavior: "smooth" });
  };

  useEffect(() => {
    if (portfolioId === prevPortfolioIdRef.current) return;
    prevPortfolioIdRef.current = portfolioId;
    setValue("advancedEKB", getExistingAdvancedEKB(selected?.tagsAsSet));
    setValue("dpClosePortfolio", false);
    if (selected) {
      setValue("dpPortType", selected.typeCode ?? "", { shouldValidate: true });
      setValue(
        "dpSumInsValue",
        selected.portfolioReport?.positionMarketValue ?? 0
      );
      setValue("dpPortfolioName", selected.name);
      setValue("dpSumAvCash", selected.portfolioReport?.accountBalance ?? 0);
      const selectedModel = selected.modelPortfolio?.linkedPortfolio?.shortName;
      if (selectedModel) {
        setValue("dpModelType", selectedModel, { shouldValidate: true });
        setValue(
          "dpModelTypeName",
          selected.modelPortfolio?.linkedPortfolio?.name ??
            selected.modelPortfolio?.name ??
            ""
        );
      } else {
        setValue("dpModelType", "NOMODEL");
        setValue("dpModelTypeName", "");
      }
      const mgmtFee = selected.fees?.find((f) => f.type === "Management");
      setValue(
        "dpManagementFee",
        mgmtFee?.feePercent !== undefined ? String(mgmtFee.feePercent) : "1.5"
      );
      const karOk = selected.accounts?.find((a) =>
        a.tagsAsSet.includes("KAR-OK")
      );
      setValue("dpAccount", karOk?.number ?? "");
    } else if (isNew) {
      setValue("dpPortType", "");
      setValue("dpPortfolioName", "Ny portefølje");
      setValue("dpSumInsValue", 0);
      setValue("dpSumAvCash", 0);
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
      setValue("dpManagementFee", "1.2");
      setValue("dpAccount", "");
      setValue("dpWithdrawal", 0);
      setValue("dpIpsWithdrawalAge", 62);
      setValue("dpIpsAnnualSavings", 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  useEffect(() => {
    if (!selected || selected.typeCode !== "23") return;
    setValue("dpIpsWithdrawalAge", ipsWdAge);
    setValue("dpIpsAnnualSavings", ipsSavingPlan);
  }, [ipsSavingPlan, ipsWdAge, selected, setValue]);

  const existingModelValue =
    selected?.modelPortfolio?.linkedPortfolio?.shortName ?? "";
  const modelOptions = useMemo(() => {
    const base = getModelOptions(portType ?? "", classification2Code);
    return existingModelValue &&
      !base.find((o) => o.value === existingModelValue)
      ? [{ value: existingModelValue, label: existingModelValue }, ...base]
      : base;
  }, [portType, existingModelValue, classification2Code]);

  useEffect(() => {
    if (
      modelType &&
      modelType !== existingModelValue &&
      !modelOptions.find((o) => o.value === modelType)
    ) {
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portType, classification2Code]);

  useEffect(() => {
    if (modelIsSet) setValue("dpFeeType", "all");
  }, [modelIsSet, setValue]);
  // Preserve saved fees on mount; update the default when model status changes.
  const prevModelIsSetRef = useRef(modelIsSet);
  useEffect(() => {
    if (isNew && modelIsSet !== prevModelIsSetRef.current) {
      setValue("dpManagementFee", modelIsSet ? "1.5" : "1.2", {
        shouldDirty: true,
      });
    }
    prevModelIsSetRef.current = modelIsSet;
  }, [isNew, modelIsSet, setValue]);
  useEffect(() => {
    setValue("ordreModul", isNoModel ? "yes" : "no");
  }, [isNoModel, setValue]);
  useEffect(() => {
    if (!isNoModel) {
      setValue("buyOrders", []);
      setValue("sellOrders", []);
      setValue("portDynamicSell", false);
      setValue("portDynamicWd", false);
    }
  }, [isNoModel, setValue]);
  useEffect(() => {
    if (isTransferAccount) return;
    setValue("moveAll", false);
    setValue("moveFrom", "");
    setValue("moveFromId", "");
    for (let index = 1; index <= MAX_INSTRUMENT_ROWS; index += 1) {
      setValue(fieldPath(`Instrument${index}`), "");
      setValue(fieldPath(`InstrumentAmt${index}`), undefined);
    }
  }, [isTransferAccount, setValue]);
  useEffect(() => {
    if (!showMinWarn) clearErrors("dpAmountToInvest");
  }, [clearErrors, showMinWarn]);

  const feeOptions = getManagementFeeOptions(
    selected?.fees?.find((f) => f.type === "Management")?.feePercent
  );

  const accountNumbers = useMemo(() => {
    const seen = new Set<string>();
    const nums: string[] = [];
    for (const p of portfolios) {
      for (const a of p.accounts ?? []) {
        if (a.tagsAsSet?.includes("KAR-OK") && !seen.has(a.number)) {
          seen.add(a.number);
          nums.push(a.number);
        }
      }
    }
    for (const accountNumber of additionalAccountNumbers) {
      const normalized = normalizeAccountNumber(accountNumber);
      if (normalized.length === 11 && !seen.has(normalized)) {
        seen.add(normalized);
        nums.push(normalized);
      }
    }
    return nums;
  }, [additionalAccountNumbers, portfolios]);

  const [priceListOpen, setPriceListOpen] = useState(false);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-muted/20 xl:flex-row xl:overflow-hidden">
      <datalist id="of-securities">
        {securities.slice(0, 500).map((s) => (
          <option key={s.id} value={s.name} />
        ))}
      </datalist>

      <nav
        aria-label="Porteføljer"
        className="flex max-h-72 min-h-0 shrink-0 flex-col border-b border-border/70 bg-card xl:max-h-none xl:w-[320px] xl:border-b-0 xl:border-r"
      >
        <PortfolioRail
          portfolios={portfolios}
          blockedPortfolioIds={blockedPortfolioIds}
          editingPortfolioId={editingPortfolioId}
          isCompany={isCompany}
          selectedId={portfolioId}
          onSelect={selectPortfolio}
          error={errMsg(formState.errors.dpPortfolioId)}
        />
      </nav>

      <section
        ref={scrollContainerRef}
        className="min-w-0 flex-1 p-4 sm:p-5 xl:overflow-y-auto"
      >
        {!portfolioId ? (
          <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card/50 px-6 py-16 text-center text-muted-foreground">
            <Plus className="size-6 opacity-30" />
            <p className="text-[13px] font-medium text-foreground">
              Velg en portefølje
            </p>
            <p className="text-[11px]">
              Velg en eksisterende portefølje i listen, eller opprett en ny.
            </p>
          </div>
        ) : (
          <div className="mx-auto max-w-5xl divide-y divide-border/60 overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm">
            <div className="space-y-4 p-4">
              <PortfolioHeader
                isCompany={isCompany}
                selected={selected}
                isNew={isNew}
                isTransferAccount={isTransferAccount}
                onJumpToTransfer={scrollToTransferSection}
                onChangeServiceType={onChangeServiceType}
              />
              <CoreFields
                targetPortfolios={targetPortfolios}
                portType={portType}
                modelType={modelType}
                modelOptions={modelOptions}
                showMinWarn={showMinWarn}
                minThreshold={minThreshold}
                totalPortValue={totalPortValue}
                portfolioMarketValue={sumAvCash + sumInsValue}
                feeOptions={feeOptions}
                modelIsSet={modelIsSet}
                priceListOpen={priceListOpen}
                onTogglePriceList={() => setPriceListOpen((v) => !v)}
                accountNumbers={accountNumbers}
                bankAccount={bankAccount}
                isNew={isNew}
                isCapguardPort={isCapguardPort}
                onCopyAmount={copyAmountToOneOff}
              />
            </div>
            {isIps && (
              <div className="bg-primary/[0.025] p-4">
                <IpsSettingsSection />
              </div>
            )}
            {classification3Code === "profEKB" && modelType === "CAPGUARDEKB" && (
              <div className="bg-primary/[0.025] p-4">
                <AdvancedEKBSection />
              </div>
            )}
            {isNoModel && (
              <div className="p-4">
                <ManualOrdersSection
                  selectedPortfolio={selected}
                  securities={securities}
                />
              </div>
            )}
            <div className="p-4">
              <AgreementsSection
                securities={securities}
                accountNumbers={accountNumbers}
              />
            </div>
            {isTransferAccount && (
              <div id="of-transfer-section" className="p-4">
                <TransferSection securities={securities} />
              </div>
            )}
          </div>
        )}
      </section>

      <aside className="min-w-0 shrink-0 space-y-3 border-t border-border/70 p-4 xl:w-[300px] xl:overflow-y-auto xl:border-l xl:border-t-0">
        <SummaryCard
          portfolios={portfolios}
          targetPortfolios={targetPortfolios}
          modelOptions={modelOptions}
          onChangeServiceType={onChangeServiceType}
        />
        <ProjectionCard portfolios={portfolios} modelsData={modelsData} />
        <PriceListCard
          open={priceListOpen}
          onToggle={() => setPriceListOpen((v) => !v)}
          totalPortValue={totalPortValue}
          modelType={modelType}
          managementFee={managementFee}
        />
      </aside>
    </div>
  );
}

// ─── Exported form ─────────────────────────────────────────────────────────────

const SERVICE_TYPE_OPTIONS = [
  {
    value: "orderExecution",
    icon: ArrowRightLeft,
    description: "Kunden har selv bestemt hva som skal kjøpes eller selges.",
  },
  {
    value: "investmentAdvice",
    icon: UserCheck,
    description: "Ordren er basert på en personlig anbefaling fra rådgiver.",
  },
] as const;

function ServiceTypeDialog({
  current,
  onSelect,
  onCancel,
}: {
  current?: string;
  onSelect: (value: (typeof SERVICE_TYPE_OPTIONS)[number]["value"]) => void;
  onCancel?: () => void;
}) {
  const firstOptionRef = useRef<HTMLButtonElement>(null);
  const currentOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    (currentOptionRef.current ?? firstOptionRef.current)?.focus();
  }, []);

  useEffect(() => {
    if (!onCancel) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="of-service-type-title"
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 text-center shadow-xl"
      >
        <h2
          id="of-service-type-title"
          className="text-base font-semibold text-foreground"
        >
          Velg tjeneste
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Hva slags tjeneste gjelder denne ordren?
        </p>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {SERVICE_TYPE_OPTIONS.map(
            ({ value, icon: Icon, description }, index) => {
              const isCurrent = current === value;
              return (
                <button
                  key={value}
                  ref={
                    isCurrent
                      ? currentOptionRef
                      : index === 0
                      ? firstOptionRef
                      : undefined
                  }
                  type="button"
                  aria-pressed={isCurrent}
                  onClick={() => onSelect(value)}
                  className={cn(
                    "relative flex flex-col items-center gap-2 rounded-xl border p-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                    isCurrent
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40 hover:bg-muted/40"
                  )}
                >
                  {isCurrent && (
                    <Check
                      aria-hidden="true"
                      className="absolute right-2.5 top-2.5 size-3.5 text-primary"
                    />
                  )}
                  <Icon aria-hidden="true" className="size-6 text-primary" />
                  <span className="text-[13px] font-semibold text-foreground">
                    {SERVICE_TYPE_LABELS[value]}
                  </span>
                  <span className="text-[11px] leading-relaxed text-muted-foreground">
                    {description}
                  </span>
                </button>
              );
            }
          )}
        </div>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {current ? "Lukk" : "Avbryt"}
          </button>
        )}
      </div>
    </div>
  );
}

export type OrderFormCleanProps = {
  portfolios: Portfolio[];
  /** Orders already in the document list, including the order being edited. */
  existingOrders?: InternalTransferOrder[];
  isCompany?: boolean;
  classification2Code?: string;
  classification3Code?: string;
  securities?: QuerySecurity[];
  modelsData?: QueryModelPosition[];
  additionalAccountNumbers?: string[];
  initialData?: Record<string, unknown>;
  documentId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDocumentCreated?: (data: Record<string, any>, documentId?: string) => void;
  onClose?: () => void;
};

export function OrderFormClean({
  portfolios,
  existingOrders = [],
  isCompany = false,
  classification2Code,
  classification3Code,
  securities = [],
  modelsData = [],
  additionalAccountNumbers = [],
  initialData,
  documentId,
  onDocumentCreated,
  onClose,
}: OrderFormCleanProps) {
  const initialPortfolio = portfolios.find(
    (portfolio) => String(portfolio.id) === String(initialData?.dpPortfolioId)
  );
  const form = useOrderForm({
    advancedEKB: getExistingAdvancedEKB(initialPortfolio?.tagsAsSet),
    ...(initialData as Parameters<typeof useOrderForm>[0]),
  });
  const blockedPortfolioIds = useMemo(
    () =>
      new Set(
        existingOrders
          .filter(
            (order) =>
              order.id !== documentId &&
              order.portfolioId !== "" &&
              order.portfolioId !== "NEW"
          )
          .map((order) => order.portfolioId)
      ),
    [existingOrders, documentId]
  );
  const editingPortfolioId = existingOrders.find(
    (order) => order.id === documentId
  )?.portfolioId;
  const serviceType = form.watch("dpServiceType");
  const [serviceDialogOpen, setServiceDialogOpen] = useState(
    !initialData?.dpServiceType
  );
  const selectedPortfolioId = form.watch("dpPortfolioId");
  const targetPortfolios = useMemo(
    () =>
      getInternalTransferTargets(
        portfolios,
        selectedPortfolioId,
        existingOrders,
        documentId
      ),
    [portfolios, selectedPortfolioId, existingOrders, documentId]
  );
  const selectedPortfolio = portfolios.find(
    (p) => p.id === Number(selectedPortfolioId)
  );

  const { data: savedProfileData } = useGetSavedQuery2<QueryProfile>(
    "advPortalProfile",
    { profileKey: `portfolio-${selectedPortfolio?.id}` },
    !selectedPortfolio
  );

  const onSubmit = (data: OrderFormData) => {
    if (blockedPortfolioIds.has(data.dpPortfolioId)) {
      form.setError("dpPortfolioId", {
        type: "manual",
        message: EXISTING_ORDER_MESSAGE,
      });
      return;
    }
    if (!data.dpServiceType) {
      setServiceDialogOpen(true);
      return;
    }
    const internalTransfer =
      data.dpWithdrawal > 0 &&
      data.dpPortfolioId !== "NEW" &&
      data.dpWithdrawalInternalTransfer === true;
    const targetPortfolio = internalTransfer
      ? getInternalTransferTargets(
          portfolios,
          data.dpPortfolioId,
          existingOrders,
          documentId
        ).find(
          (portfolio) =>
            String(portfolio.id) === data.dpWithdrawalTargetPortfolioId
        )
      : undefined;
    if (internalTransfer && !targetPortfolio) {
      form.setError("dpWithdrawalTargetPortfolioId", {
        type: "manual",
        message:
          "Velg en annen Capguard-portefølje hos samme kunde. Nye porteføljer må først legges til som en ordre.",
      });
      return;
    }
    const minThreshold = getMinimumMandateAmount(data.dpModelType);
    const totalPortValue =
      (Number(data.dpSumAvCash) || 0) +
      (Number(data.dpSumInsValue) || 0) +
      (Number(data.dpAmountToInvest) || 0);
    if (
      data.dpPortfolioId === "NEW" &&
      minThreshold > 0 &&
      totalPortValue > 0 &&
      totalPortValue < minThreshold
    ) {
      form.setError("dpAmountToInvest", {
        type: "manual",
        message: `${
          data.dpModelType === "CAPGUARDEKB" ? "Aktiv EKB" : "Aktiv Eiendom"
        } krever en minimumsverdi på NOK ${fmt(minThreshold)}.`,
      });
      return;
    }
    form.clearErrors("dpAmountToInvest");

    if (data.dpModelType === "NOMODEL") {
      const buyRows = (data.buyOrders ?? []) as Array<{
        instrumentName?: string;
        securityId?: number | null;
        amountInput?: string;
      }>;
      const invalidBuyRows = buyRows.filter(
        (row) =>
          row.instrumentName &&
          !securities.some((security) => security.id === row.securityId)
      );
      if (invalidBuyRows.length > 0) {
        form.setError("buyOrders", {
          type: "manual",
          message: "Velg et fond eller instrument fra listen.",
        });
        return;
      }
      const sumBuy = buyRows
        .filter((r) => r.instrumentName)
        .reduce((s, r) => s + (parseFloat(r.amountInput ?? "") || 0), 0);
      if (
        data.dpAmountToInvest > 0 &&
        Math.abs(sumBuy - data.dpAmountToInvest) > 0.01
      ) {
        form.setError("buyOrders", {
          type: "manual",
          message: `Kjøpsordre totalt (NOK ${fmt(
            sumBuy
          )}) må tilsvare investeringsbeløpet (${fmt(data.dpAmountToInvest)})`,
        });
        return;
      }
      const oneOffAmount = Number(data.dpOnOffAmount) || 0;
      if (oneOffAmount > 0 && sumBuy + 0.005 < oneOffAmount) {
        form.setError("buyOrders", {
          type: "manual",
          message: `Kjøpsordre totalt (${fmt(
            sumBuy
          )}) må være minst engangstrekket (${fmt(oneOffAmount)})`,
        });
        return;
      }
      form.clearErrors("buyOrders");
    }

    const selectedPortfolio = portfolios.find(
      (p) => p.id === Number(data.dpPortfolioId)
    );
    const selectedModelName =
      data.dpModelType === "NOMODEL"
        ? ""
        : getModelOptions(data.dpPortType, classification2Code).find(
            (option) => option.value === data.dpModelType
          )?.label ??
          (selectedPortfolio?.modelPortfolio?.linkedPortfolio?.shortName ===
          data.dpModelType
            ? selectedPortfolio?.modelPortfolio?.linkedPortfolio?.name ??
              selectedPortfolio?.modelPortfolio?.name ??
              data.dpModelType
            : MODEL_DISPLAY_NAMES[data.dpModelType] ?? data.dpModelType);
    const existingAdvancedEKB = getExistingAdvancedEKB(selectedPortfolio?.tagsAsSet);
    const advancedEKBEnabled =
      classification3Code === "profEKB" && data.dpModelType === "CAPGUARDEKB";
    const advancedEKB = advancedEKBEnabled
      ? data.advancedEKB || existingAdvancedEKB
      : "";
    const AdvancedEKBchange =
      advancedEKBEnabled &&
      (data.dpPortfolioId === "NEW" || advancedEKB !== existingAdvancedEKB);

    onDocumentCreated?.(
      {
        existData: buildExistData(selectedPortfolio, savedProfileData),
        newData: {
          ...initialData,
          ...data,
          existingAdvancedEKB,
          advancedEKB,
          AdvancedEKBchange,
          dpClosePortfolio:
            !!selectedPortfolio &&
            isFullWithdrawal(
              data.dpWithdrawal,
              data.dpSumAvCash + data.dpSumInsValue
            ) &&
            data.dpClosePortfolio === true,
          dpModelTypeName: selectedModelName,
          dpWithdrawalInternalTransfer: internalTransfer,
          dpWithdrawalTargetPortfolioId: targetPortfolio
            ? String(targetPortfolio.id)
            : "",
          dpWithdrawalTargetPortfolioShortName:
            targetPortfolio?.shortName ?? "",
          dpWithdrawalTargetPortfolioName: targetPortfolio?.name ?? "",
          sellOrders: data.sellOrders.filter(
            (row) => parseFloat(row.sellQty.replace(",", ".")) > 0
          ),
        },
      },
      documentId
    );
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="relative flex h-full min-h-0 flex-col"
      >
        <div
          data-testid="order-form-content"
          aria-hidden={serviceDialogOpen}
          {...(serviceDialogOpen ? { inert: true } : {})}
          className={cn(
            "flex min-h-0 flex-1 flex-col transition-[filter]",
            serviceDialogOpen && "pointer-events-none select-none blur-sm"
          )}
        >
          <OrderFormCleanContent
            portfolios={portfolios}
            targetPortfolios={targetPortfolios}
            blockedPortfolioIds={blockedPortfolioIds}
            editingPortfolioId={editingPortfolioId}
            securities={securities}
            isCompany={isCompany}
            classification2Code={classification2Code}
            classification3Code={classification3Code}
            modelsData={modelsData}
            profile={savedProfileData}
            additionalAccountNumbers={additionalAccountNumbers}
            onChangeServiceType={() => setServiceDialogOpen(true)}
          />
          <div className="z-10 shrink-0 border-t border-border bg-background px-4 py-3">
            {Object.keys(form.formState.errors).length > 0 && (
              <div
                role="alert"
                className="mb-3 max-h-32 overflow-y-auto text-sm text-destructive"
              >
                <p className="font-medium">
                  Kan ikke lagre ordren. Kontroller disse feltene:
                </p>
                <ul className="list-inside list-disc">
                  {Object.entries(form.formState.errors).map(
                    ([field, error]) => (
                      <li key={field}>
                        {field}:{" "}
                        {errMsg(error) ?? "Kontroller verdien i feltet."}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-3">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
                >
                  Avbryt
                </button>
              )}
              <button
                type="submit"
                className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
              >
                {documentId ? "Lagre endringer" : "Legg til dokument"}
              </button>
            </div>
          </div>
        </div>
        {serviceDialogOpen && (
          <ServiceTypeDialog
            current={serviceType}
            onSelect={(value) => {
              form.setValue("dpServiceType", value, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setServiceDialogOpen(false);
            }}
            onCancel={serviceType ? () => setServiceDialogOpen(false) : onClose}
          />
        )}
      </form>
    </FormProvider>
  );
}
